import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { hasPerm, getUser } from "@/lib/auth";
import {
  WorkspaceShell, WorkspaceHeader, WorkspaceQuickLink as QuickLink,
  WorkspaceDetailField as DetailField, normalizeUrl,
} from "./workspace/WorkspaceShell";
import { WorkspaceDatePicker as DatePicker } from "./workspace/WorkspaceDatePicker";
import { fmtDate, today } from "./workspace/dateUtils";
import { CandidateWorkspace } from "./CandidateWorkspace";
import {
  crmMeta, crmAssignees, crmListProspects, crmViewCounts, crmStats, crmFacets,
  crmGetProspect, crmUpdateProspect, crmLogActivity, crmConvert, crmBulk,
  crmDeleteProspect, crmImportDetect, crmImport, crmExport, crmCreateProspect,
  crmDeals, leadRecommendedCandidates, emailTemplatesList, crmRenderEmail, crmTrackEmail,
  campaignCreate, crmUpdateDeal, crmCampaignFunnel, addSuppression,
} from "@/lib/api";
import {
  Loader2, Search, Upload, Download, Users, Target, Phone, Mail, Linkedin,
  Calendar, TrendingUp, Building2, ArrowUpDown, ChevronLeft, ChevronRight,
  Trash2, X, Plus, CheckCircle2, ExternalLink, Star, Clock, LayoutDashboard,
  ListFilter, UserCheck, FileText, Sparkles, ChevronDown, PenLine, Send,
  MailOpen, Eye, Rocket, MousePointerClick, ClipboardCheck, Copy, Filter,
  RefreshCw, Ban, Check, Globe, AlertTriangle, ShieldCheck,
} from "lucide-react";
import {
  prospectTimeInfo, resolveTimeZone, getUserTimeZone, formatZoneTime,
  zoneHourDiff, diffLabel, zoneHour, isOutsideBusinessHours,
} from "@/lib/localTime";

// Ticking clock: re-render roughly every minute so displayed local times stay
// current without a full data refetch.
const useMinuteTick = () => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);
};

// Prospect-local time line for the Workspace (Company Information section).
const ProspectLocalTime = ({ city, state }) => {
  useMinuteTick();
  const info = prospectTimeInfo(city, state);
  return (
    <div className="flex items-center justify-between gap-2 py-1" data-testid="crm-ws-local-time">
      <span className="flex items-center gap-2 text-white/45 text-sm"><Globe size={13} /> Local Time</span>
      {info.known ? (
        <span className="text-white/85 text-sm text-right" data-testid="crm-ws-local-time-value">
          {info.timeStr}
          <span className="text-white/45"> · {info.diffText}</span>
        </span>
      ) : (
        <span className="text-white/50 text-sm" data-testid="crm-ws-local-time-value">Unknown</span>
      )}
    </div>
  );
};

// Recipient-local delivery timing shown in the email preview. Since emails are
// composed to send now, "delivery time" reflects the current moment. Adds a
// non-blocking warning when it's outside the recipient's 8:00 AM–6:00 PM local.
const EmailRecipientTime = ({ city, state }) => {
  useMinuteTick();
  const now = new Date();
  const userTz = getUserTimeZone();
  const tz = resolveTimeZone(city, state);
  return (
    <div className="rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3" data-testid="crm-email-recipient-time">
      <div className="text-white/40 text-xs mb-1">Delivery Timing</div>
      <div className="text-white/80 text-sm" data-testid="crm-email-your-time">
        Your time: {formatZoneTime(userTz, now) || "\u2014"}
      </div>
      {tz ? (() => {
        const diff = zoneHourDiff(tz, userTz, now);
        const hour = zoneHour(tz, now);
        const warn = isOutsideBusinessHours(hour);
        return (
          <>
            <div className="text-white/80 text-sm" data-testid="crm-email-recipient-local">
              Recipient: {formatZoneTime(tz, now)} <span className="text-white/45">({diffLabel(diff)})</span>
            </div>
            {warn && (
              <div className="mt-2 flex items-start gap-2 rounded-md border border-[#F5B94A]/25 bg-[#F5B94A]/10 px-3 py-2" data-testid="crm-email-time-warning">
                <AlertTriangle size={14} className="text-[#F5B94A] mt-0.5 shrink-0" />
                <span className="text-[#F5B94A]/90 text-xs">
                  Heads up — it's currently outside the recipient's typical business hours
                  (before 8:00 AM or after 6:00 PM local). You can still send.
                </span>
              </div>
            )}
          </>
        );
      })() : (
        <div className="text-white/50 text-sm" data-testid="crm-email-recipient-local">
          Recipient local time: Unknown
        </div>
      )}
    </div>
  );
};

// --- theme helpers ---------------------------------------------------------
const statusColor = (s) => ({
  New: "bg-white/10 text-white/70 border-white/15",
  Attempted: "bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30",
  Contacted: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30",
  "Follow-Up": "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30",
  Interested: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30",
  Qualified: "bg-[#2EE59D]/20 text-[#2EE59D] border-[#2EE59D]/40",
  "Not Interested": "bg-[#FF5C7A]/15 text-[#FF5C7A] border-[#FF5C7A]/30",
  Closed: "bg-white/5 text-white/40 border-white/10",
}[s] || "bg-white/10 text-white/70 border-white/15");

const QUICK_VIEWS = [
  ["all", "All Prospects"], ["my", "My Leads"], ["new", "New"],
  ["follow_ups", "Follow-Ups"], ["interested", "Interested"],
  ["qualified", "Qualified"], ["meetings", "Meetings"], ["closed", "Closed"],
];

const inputCls = "bg-[#0A0F1E] border-white/10 h-10 text-white";


// --- lightweight, quote-aware CSV parser -----------------------------------
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let i = 0;
  let inQuotes = false;
  const pushField = () => { row.push(field); field = ""; };
  const pushRow = () => { rows.push(row); row = []; };
  // Strip BOM
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { pushField(); i++; continue; }
    if (c === "\r") { i++; continue; }
    if (c === "\n") { pushField(); pushRow(); i++; continue; }
    field += c; i++;
  }
  if (field.length > 0 || row.length > 0) { pushField(); pushRow(); }
  // Drop fully-empty trailing rows
  return rows.filter((r) => r.some((v) => (v || "").trim() !== ""));
}

// ===========================================================================
// Import Wizard
// ===========================================================================
const ImportWizard = ({ token, meta, assignees, onClose, onDone }) => {
  const [step, setStep] = useState(1); // 1 upload, 2 map, 3 options, 4 result
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState({}); // header -> field|null
  const [campaign, setCampaign] = useState("");
  const [localCampaigns, setLocalCampaigns] = useState(meta?.active_campaigns || []);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState("");
  const createCampaignInline = async () => {
    const name = newCampaign.trim();
    if (!name) return;
    try {
      const res = await campaignCreate(token, { name });
      const nm = res.campaign.name;
      setLocalCampaigns((cs) => Array.from(new Set([...cs, nm])).sort());
      setCampaign(nm);
      setCreatingCampaign(false);
      setNewCampaign("");
      toast.success("Campaign created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create campaign");
    }
  };
  const [assignedTo, setAssignedTo] = useState("unassigned");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const fields = meta?.mappable_fields || [];

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.length < 2) {
        toast.error("CSV appears to be empty or has no data rows");
        return;
      }
      const hdrs = parsed[0].map((h) => h.trim());
      const body = parsed.slice(1);
      setHeaders(hdrs);
      setDataRows(body);
      const det = await crmImportDetect(token, hdrs);
      setMapping(det.mapping || {});
      setStep(2);
    } catch (e) {
      toast.error("Could not read the CSV file");
    }
  };

  // Build mapped rows + compute preview stats client-side
  const mappedRows = useMemo(() => {
    const colIdx = {};
    headers.forEach((h, idx) => { colIdx[h] = idx; });
    return dataRows.map((r) => {
      const obj = {};
      Object.entries(mapping).forEach(([h, fld]) => {
        if (fld) obj[fld] = (r[colIdx[h]] || "").trim();
      });
      return obj;
    });
  }, [headers, dataRows, mapping]);

  const preview = useMemo(() => {
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const seen = new Set();
    let valid = 0, dup = 0, rejected = 0;
    mappedRows.forEach((r) => {
      const e = (r.email || "").trim().toLowerCase();
      if (!emailRe.test(e)) { rejected++; return; }
      if (seen.has(e)) { dup++; return; }
      seen.add(e); valid++;
    });
    return { total: mappedRows.length, valid, dup, rejected };
  }, [mappedRows]);

  const emailMapped = Object.values(mapping).includes("email");

  const commit = async () => {
    setBusy(true);
    try {
      const res = await crmImport(token, {
        rows: mappedRows,
        campaign: campaign.trim() || null,
        assigned_to: assignedTo === "unassigned" ? null : assignedTo,
        source: "Apollo.io",
      });
      setResult(res);
      setStep(4);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0E1424] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="crm-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={18} className="text-[#66B2FF]" /> Import Prospects
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs text-white/50 mb-2">
          {["Upload", "Map Fields", "Options", "Done"].map((label, idx) => (
            <React.Fragment key={label}>
              <span className={step === idx + 1 ? "text-[#66B2FF] font-medium" : ""}>{label}</span>
              {idx < 3 && <span className="text-white/20">→</span>}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div className="py-6">
            <div
              className="border-2 border-dashed border-white/15 rounded-xl p-10 text-center cursor-pointer hover:border-[#66B2FF]/40 transition-colors"
              onClick={() => fileRef.current?.click()}
              data-testid="crm-import-dropzone"
            >
              <Upload size={32} className="mx-auto text-white/40 mb-3" />
              <div className="text-white/80 font-medium">Upload an Apollo.io CSV export</div>
              <div className="text-white/40 text-sm mt-1">Click to browse — column names are auto-detected</div>
              <input
                ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                data-testid="crm-import-file"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-white/60 text-sm">
              Map your CSV columns to prospect fields. Auto-detected columns are pre-filled — adjust any as needed.
              <span className="text-white/40"> ({dataRows.length} data rows found)</span>
            </p>
            <div className="rounded-xl border border-white/10 divide-y divide-white/10 max-h-[46vh] overflow-y-auto">
              {headers.map((h) => (
                <div key={h} className="flex items-center gap-3 px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-white/80 text-sm truncate">{h}</div>
                    <div className="text-white/35 text-xs truncate">
                      e.g. {(dataRows[0] && dataRows[0][headers.indexOf(h)]) || "—"}
                    </div>
                  </div>
                  <ArrowUpDown size={14} className="text-white/25" />
                  <div className="w-48">
                    <Select
                      value={mapping[h] || "ignore"}
                      onValueChange={(v) => setMapping((m) => ({ ...m, [h]: v === "ignore" ? null : v }))}
                    >
                      <SelectTrigger className="bg-[#0A0F1E] border-white/10 h-9 text-white text-sm" data-testid={`crm-map-${h}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                        <SelectItem value="ignore">Ignore column</SelectItem>
                        {fields.map((f) => (
                          <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            {!emailMapped && (
              <div className="text-[#F5B94A] text-sm flex items-center gap-2">
                <Mail size={14} /> Map a column to <b>Email</b> — it is required for import & duplicate detection.
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="ghost" className="text-white/60" onClick={() => setStep(1)}>Back</Button>
              <Button
                className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90"
                disabled={!emailMapped}
                onClick={() => setStep(3)}
                data-testid="crm-import-next-options"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm">Campaign</label>
                {creatingCampaign ? (
                  <div className="flex gap-2 mt-1">
                    <Input autoFocus className={inputCls} placeholder="New campaign name"
                      value={newCampaign} onChange={(e) => setNewCampaign(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") createCampaignInline(); }}
                      data-testid="crm-import-campaign-new" />
                    <Button size="sm" className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90 h-9" onClick={createCampaignInline} data-testid="crm-import-campaign-create">Create</Button>
                    <Button size="sm" variant="ghost" className="text-white/50 h-9" onClick={() => { setCreatingCampaign(false); setNewCampaign(""); }}>Cancel</Button>
                  </div>
                ) : (
                  <Select value={campaign || "none"}
                    onValueChange={(v) => { if (v === "__create__") { setCreatingCampaign(true); } else { setCampaign(v === "none" ? "" : v); } }}>
                    <SelectTrigger className={inputCls + " mt-1"} data-testid="crm-import-campaign"><SelectValue placeholder="No campaign" /></SelectTrigger>
                    <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                      <SelectItem value="none">No campaign</SelectItem>
                      {localCampaigns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="__create__" className="text-[#66B2FF]">＋ Create new campaign…</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <label className="text-white/60 text-sm">Assign all to</label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger className={inputCls + " mt-1"} data-testid="crm-import-assignee">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {assignees.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {[["Total rows", preview.total, "text-white"],
                ["Valid", preview.valid, "text-[#2EE59D]"],
                ["Duplicates", preview.dup, "text-[#F5B94A]"],
                ["Rejected", preview.rejected, "text-[#FF5C7A]"]].map(([label, val, color]) => (
                <div key={label} className="rounded-xl border border-white/10 p-3 text-center bg-[#0A0F1E]">
                  <div className={`text-2xl font-bold tabular-nums ${color}`}>{val}</div>
                  <div className="text-white/45 text-xs mt-1">{label}</div>
                </div>
              ))}
            </div>
            <p className="text-white/40 text-xs">
              New imported prospects default to status <b className="text-white/60">New</b>. Duplicates
              (matching email) and rows without a valid email are skipped — the rest still import.
            </p>

            <div className="flex justify-between">
              <Button variant="ghost" className="text-white/60" onClick={() => setStep(2)}>Back</Button>
              <Button
                className="bg-[#2EE59D] text-[#06231A] hover:bg-[#2EE59D]/90"
                disabled={busy || preview.valid === 0}
                onClick={commit}
                data-testid="crm-import-confirm"
              >
                {busy ? <Loader2 className="animate-spin" size={16} /> : `Import ${preview.valid} Prospects`}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="py-6 text-center" data-testid="crm-import-result">
            <CheckCircle2 size={48} className="mx-auto text-[#2EE59D]" />
            <div className="text-xl font-semibold mt-3">Import Complete</div>
            <div className="grid grid-cols-4 gap-3 mt-5">
              {[["Total", result.total], ["Created", result.created],
                ["Duplicates", result.duplicates], ["Rejected", result.rejected]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-white/10 p-3 bg-[#0A0F1E]">
                  <div className="text-2xl font-bold tabular-nums text-white">{v}</div>
                  <div className="text-white/45 text-xs mt-1">{l}</div>
                </div>
              ))}
            </div>
            <Button className="mt-6 bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" onClick={onClose} data-testid="crm-import-done">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
// Prospect Email quick action — enhances the existing mailto behavior with an
// optional template flow. Compose Blank keeps the original mailto: behavior;
// "Email from Template" resolves merge fields server-side (RBAC-scoped) and
// previews the result before launching the user's default mail client.
// ===========================================================================
const pillCls =
  "inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-sm transition-colors text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

const buildMailto = (to, subject, body) => {
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body.replace(/\n/g, "\r\n"))}`);
  return `mailto:${to || ""}${params.length ? `?${params.join("&")}` : ""}`;
};

// Plain-text version of an HTML string (for a clipboard text/plain fallback).
const htmlToPlain = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.innerText || div.textContent || "";
};

// Escape plain text and convert it to minimal HTML (newlines -> <br/>) so a
// blank/compose email carries a valid HTML body for the tracking pixel to
// attach to — reusing the exact same pixel architecture as templated emails.
const plainToHtml = (text) => {
  const esc = (text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc.replace(/\r\n|\r|\n/g, "<br/>");
};

// Format an ISO datetime for engagement display, e.g. "Aug 18, 10:42 AM".
const fmtDateTime = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
};

// Behavioral engagement level -> display meta. Level comes from the backend
// (email_engagement); a client fallback derives it if absent.
const ENGAGEMENT_META = {
  "Highly Engaged": { cls: "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30", rank: 3 },
  "Engaged": { cls: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30", rank: 2 },
  "Opened": { cls: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30", rank: 1 },
  "None": { cls: "bg-white/5 text-white/40 border-white/15", rank: 0 },
};

const engagementLevel = (p) => {
  if (p?.email_engagement) return p.email_engagement;
  const count = p?.email_open_count || 0;
  if (count <= 0) return "None";
  let span = 0;
  try {
    if (p.email_first_opened_at && p.email_last_opened_at) {
      span = (new Date(p.email_last_opened_at) - new Date(p.email_first_opened_at)) / 1000;
    }
  } catch { /* ignore */ }
  if (count >= 3 && span >= 3600) return "Highly Engaged";
  if (count >= 2 && span >= 600) return "Engaged";
  return "Opened";
};


// Copy rich HTML onto the clipboard so it can be pasted into an email client
// with formatting intact. Falls back to an execCommand copy for older browsers.
const copyRichHtml = async (html) => {
  const plain = htmlToPlain(html);
  try {
    if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
      const item = new window.ClipboardItem({
        "text/html": new Blob([html || ""], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
      });
      await navigator.clipboard.write([item]);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const div = document.createElement("div");
    div.contentEditable = "true";
    div.innerHTML = html || "";
    div.style.position = "fixed";
    div.style.left = "-9999px";
    document.body.appendChild(div);
    const range = document.createRange();
    range.selectNodeContents(div);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    const ok = document.execCommand("copy");
    sel.removeAllRanges();
    document.body.removeChild(div);
    return ok;
  } catch {
    return false;
  }
};

const SUPPRESSION_REASONS = ["Recipient unsubscribed", "Manual opt-out", "Hard bounce", "Spam complaint"];

// Admin/Sales action to manually add a recipient to the GLOBAL suppression list
// (e.g. they replied "remove me"). Reuses the same suppression the one-click
// unsubscribe link uses, so it applies across every campaign.
const SuppressMarketingAction = ({ token, prospect, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("Manual opt-out");
  const [busy, setBusy] = useState(false);
  const already = prospect?.marketing_status && prospect.marketing_status !== "Subscribed";
  if (already) return null; // already suppressed; the header badge shows status

  const submit = async () => {
    setBusy(true);
    try {
      await addSuppression(token, { prospect_id: prospect.id, email: prospect.email, reason });
      toast.success("Marketing email suppressed for this recipient.");
      setOpen(false);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not suppress this address");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={pillCls}
        data-testid="crm-suppress-marketing"
        onClick={() => { setReason("Manual opt-out"); setOpen(true); }}
      >
        <Ban size={14} /> Suppress
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#0E1424] border-white/10 text-white" data-testid="crm-suppress-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <Ban size={18} className="text-[#F5B94A]" /> Suppress Marketing Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <p className="text-white/60 text-sm">
              This adds <b className="text-white/80">{prospect.email}</b> to the global suppression list.
              They will be blocked from all future Rendeavored marketing emails across every campaign.
            </p>
            <div>
              <div className="text-white/60 text-xs mb-1">Reason</div>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="bg-[#0A0F1E] border-white/10 text-white h-10" data-testid="crm-suppress-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                  {SUPPRESSION_REASONS.map((r) => (
                    <SelectItem key={r} value={r} className="focus:bg-white/10 focus:text-white">{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="crm-suppress-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy} className="bg-[#F5B94A] hover:bg-[#F5B94A]/90 text-[#0A0F1E]" data-testid="crm-suppress-confirm">
              {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Ban size={15} className="mr-1" />} Suppress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};


const ProspectEmailAction = ({ token, prospect, onSent }) => {
  const email = prospect?.email;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [templates, setTemplates] = useState(null); // null=loading
  const [templateQuery, setTemplateQuery] = useState("");
  const [selected, setSelected] = useState(null);    // template being previewed
  const [preview, setPreview] = useState(null);       // { to, subject, body }
  const [rendering, setRendering] = useState(false);
  const [sending, setSending] = useState(false);
  // Blank (compose-from-scratch) email dialog state.
  const [blankOpen, setBlankOpen] = useState(false);
  const [blankSubject, setBlankSubject] = useState("");
  const [blankBody, setBlankBody] = useState("");

  // Disabled state (no email) — mirror the original QuickLink disabled pill.
  if (!email) {
    return (
      <QuickLink href={null} icon={Mail} label="Email" testid="crm-quick-email"
        disabledTip="No email address available." />
    );
  }

  // CAN-SPAM: block outreach entirely for opted-out recipients.
  if (prospect?.marketing_status && prospect.marketing_status !== "Subscribed") {
    return (
      <QuickLink href={null} icon={Ban} label="Email suppressed" testid="crm-quick-email"
        disabledTip="This recipient has opted out of Rendeavored marketing communications." />
    );
  }

  // Shared tracked-send path used by BOTH the template flow and the blank
  // compose flow. Creates a tracked email activity (single tracking system),
  // appends the CAN-SPAM unsubscribe footer + the returned 1x1 pixel to the
  // HTML, copies the rich body to the clipboard (mailto cannot carry
  // HTML/images), and launches the mail client. We never claim the message was
  // "Sent" — the backend records it as "Draft Launched". Returns false if the
  // recipient is suppressed (409) so the caller can abort.
  const launchTracked = async ({ templateId, templateName, subject, recipient, bodyHtml, blank }) => {
    let html = bodyHtml || "";
    const to = recipient || email;
    let track;
    try {
      track = await crmTrackEmail(token, prospect.id, {
        template_id: templateId || null,
        template_name: templateName || null,
        subject: subject || "",
        recipient: to,
        blank: !!blank,
      });
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(e?.response?.data?.detail
          || "Email suppressed \u2014 this recipient has opted out of Rendeavored marketing communications.");
        if (onSent) onSent();
        return false;
      }
      // Tracking is best-effort for other errors — never block the workflow.
      console.warn("track-email failed", e);
    }
    // Compliance unsubscribe footer, then the invisible tracking pixel.
    if (track?.footer_html) html += track.footer_html;
    if (track?.pixel_url) {
      // Accessibility-friendly, Outlook-compatible 1x1 pixel. Uses the existing
      // dynamically generated tracking URL — open-event recording is unchanged.
      html += `<img src="${track.pixel_url}" width="1" height="1" alt="" border="0" aria-hidden="true" style="display:block;width:1px;height:1px;border:0;margin:0;padding:0;overflow:hidden;" />`;
    }
    const copied = await copyRichHtml(html);
    // mailto cannot carry HTML, so the formatted body (incl. footer + pixel) is
    // placed on the clipboard for the user to paste (one Ctrl/Cmd+V).
    window.location.href = buildMailto(to, subject, "");
    if (copied) {
      toast.success("Draft launched \u2014 paste the copied body into your email (Ctrl/Cmd+V), then send.");
    } else {
      toast.info("Draft launched. Copy the email body from the preview into your draft.");
    }
    if (onSent) onSent();
    return true;
  };

  const composeBlank = () => {
    setBlankSubject("");
    setBlankBody("");
    setBlankOpen(true);
  };

  const sendBlank = async () => {
    if (sending) return;
    setSending(true);
    try {
      await launchTracked({
        templateId: null,
        templateName: "Blank Email",
        subject: blankSubject.trim(),
        recipient: email,
        bodyHtml: plainToHtml(blankBody),
        blank: true,
      });
    } finally {
      setSending(false);
      setBlankOpen(false);
    }
  };

  const openTemplatePicker = async () => {
    setPickerOpen(true);
    setSelected(null);
    setPreview(null);
    setTemplates(null);
    setTemplateQuery("");
    try {
      const res = await emailTemplatesList(token);
      // Only active templates are usable for outbound emails.
      setTemplates((res.templates || []).filter((t) => t.active));
    } catch {
      toast.error("Failed to load templates");
      setTemplates([]);
    }
  };

  const chooseTemplate = async (tpl) => {
    setSelected(tpl);
    setPreview(null);
    setRendering(true);
    try {
      const res = await crmRenderEmail(token, prospect.id, tpl.id);
      setPreview(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not render this template");
      setSelected(null);
    } finally {
      setRendering(false);
    }
  };

  const openDraft = async () => {
    if (!preview || sending) return;
    setSending(true);
    try {
      await launchTracked({
        templateId: selected?.id || null,
        templateName: selected?.name || preview.template_name || null,
        subject: preview.subject || "",
        recipient: preview.to || email,
        bodyHtml: preview.body_html || "",
        blank: false,
      });
    } finally {
      setSending(false);
      setPickerOpen(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={pillCls} data-testid="crm-quick-email">
            <Mail size={14} /> Email <ChevronDown size={13} className="opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#0E1424] border-white/10 text-white">
          <DropdownMenuItem
            onClick={composeBlank}
            className="cursor-pointer focus:bg-white/10 focus:text-white"
            data-testid="crm-email-compose-blank"
          >
            <PenLine size={14} className="mr-2 text-white/60" /> Compose Blank Email
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={openTemplatePicker}
            className="cursor-pointer focus:bg-white/10 focus:text-white"
            data-testid="crm-email-from-template"
          >
            <FileText size={14} className="mr-2 text-white/60" /> Email from Template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl bg-[#0E1424] border-white/10 text-white" data-testid="crm-email-template-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <Mail size={18} className="text-[#1E6BFF]" /> Email from Template
            </DialogTitle>
          </DialogHeader>

          {!selected ? (
            <div className="mt-1">
              <div className="text-white/50 text-sm mb-3">
                Choose a template — merge fields fill from <b className="text-white/70">{prospect.name}</b>.
              </div>
              {templates === null ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="animate-spin text-white/50" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-10 text-white/50" data-testid="crm-email-templates-empty">
                  No active email templates are available.
                </div>
              ) : (() => {
                const q = templateQuery.trim().toLowerCase();
                const filtered = templates.filter(
                  (t) => !q
                    || (t.name || "").toLowerCase().includes(q)
                    || (t.subject || "").toLowerCase().includes(q)
                );
                return (
                  <div>
                    <div className="relative mb-3">
                      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                      <Input
                        autoFocus
                        value={templateQuery}
                        onChange={(e) => setTemplateQuery(e.target.value)}
                        placeholder="Search templates by name or subject..."
                        data-testid="crm-email-template-search"
                        className="h-10 pl-9 pr-9 bg-[#0A0F1E] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-[#1E6BFF]/50"
                      />
                      {templateQuery && (
                        <button
                          type="button"
                          onClick={() => setTemplateQuery("")}
                          data-testid="crm-email-template-search-clear"
                          aria-label="Clear search"
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                        >
                          <X size={15} />
                        </button>
                      )}
                    </div>
                    {filtered.length === 0 ? (
                      <div className="text-center py-10 text-white/45 text-sm" data-testid="crm-email-templates-no-match">
                        No templates match &ldquo;{templateQuery}&rdquo;.
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                        {filtered.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => chooseTemplate(t)}
                            data-testid={`crm-email-template-option-${t.id}`}
                            className="w-full text-left rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3 hover:border-[#1E6BFF]/50 hover:bg-white/5 transition-colors"
                          >
                            <div className="font-medium text-white">{t.name}</div>
                            {t.subject && <div className="text-white/50 text-sm truncate mt-0.5">{t.subject}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="mt-1">
              {rendering ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="animate-spin text-white/50" />
                </div>
              ) : preview ? (
                <div className="space-y-3" data-testid="crm-email-preview">
                  <div className="rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3">
                    <div className="text-white/40 text-xs">To</div>
                    <div className="text-white/85 text-sm break-all" data-testid="crm-email-preview-to">{preview.to || "\u2014"}</div>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3">
                    <div className="text-white/40 text-xs">Subject</div>
                    <div className="text-white/85 text-sm" data-testid="crm-email-preview-subject">{preview.subject || "\u2014"}</div>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3">
                    <div className="text-white/40 text-xs mb-1">Body</div>
                    {preview.body_html ? (
                      <div
                        className="rd-rich-content text-white/85 text-sm leading-relaxed max-h-[32vh] overflow-y-auto"
                        data-testid="crm-email-preview-body"
                        dangerouslySetInnerHTML={{ __html: preview.body_html }}
                      />
                    ) : (
                      <div className="text-white/85 text-sm" data-testid="crm-email-preview-body">
                        {"\u2014"}
                      </div>
                    )}
                    {preview.footer_html && (
                      <div
                        className="rd-rich-content mt-3"
                        data-testid="crm-email-preview-footer"
                        dangerouslySetInnerHTML={{ __html: preview.footer_html }}
                      />
                    )}
                  </div>
                  <div className="text-white/40 text-xs flex items-start gap-1.5" data-testid="crm-email-compliance-note">
                    <ShieldCheck size={13} className="text-[#2EE59D] mt-0.5 shrink-0" />
                    A CAN-SPAM unsubscribe footer and tracking pixel are added automatically when you launch.
                  </div>
                  <EmailRecipientTime city={prospect.city} state={prospect.state} />
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter className="mt-2">
            {selected && (
              <Button
                variant="ghost"
                onClick={() => { setSelected(null); setPreview(null); }}
                className="text-white/60 hover:text-white hover:bg-white/5 mr-auto"
                data-testid="crm-email-back"
              >
                <ChevronLeft size={15} className="mr-1" /> Back
              </Button>
            )}
            <Button variant="ghost" onClick={() => setPickerOpen(false)} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="crm-email-cancel">
              Cancel
            </Button>
            {selected && preview && !rendering && (
              <Button onClick={openDraft} disabled={sending} className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" data-testid="crm-email-open-draft">
                {sending ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Send size={15} className="mr-1" />} Open Email Draft
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blank / compose-from-scratch email — still fully tracked. */}
      <Dialog open={blankOpen} onOpenChange={setBlankOpen}>
        <DialogContent className="max-w-2xl bg-[#0E1424] border-white/10 text-white" data-testid="crm-email-blank-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <PenLine size={18} className="text-[#1E6BFF]" /> Compose Blank Email
            </DialogTitle>
          </DialogHeader>
          <div className="mt-1 space-y-3">
            <div className="rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3">
              <div className="text-white/40 text-xs">To</div>
              <div className="text-white/85 text-sm break-all" data-testid="crm-email-blank-to">{email}</div>
            </div>
            <div>
              <div className="text-white/50 text-xs mb-1">Subject</div>
              <Input
                autoFocus
                value={blankSubject}
                onChange={(e) => setBlankSubject(e.target.value)}
                placeholder="Email subject..."
                data-testid="crm-email-blank-subject"
                className="h-10 bg-[#0A0F1E] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-[#1E6BFF]/50"
              />
            </div>
            <div>
              <div className="text-white/50 text-xs mb-1">Body</div>
              <Textarea
                value={blankBody}
                onChange={(e) => setBlankBody(e.target.value)}
                placeholder="Write your message..."
                rows={8}
                data-testid="crm-email-blank-body"
                className="bg-[#0A0F1E] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-[#1E6BFF]/50 resize-y min-h-[160px]"
              />
            </div>
            <div className="text-white/40 text-xs flex items-start gap-1.5">
              <AlertTriangle size={13} className="text-[#F5B94A] mt-0.5 shrink-0" />
              A CAN-SPAM unsubscribe footer and an invisible tracking pixel are added automatically. Paste the copied body into your email (Ctrl/Cmd+V) before sending so opens are tracked.
            </div>
            <EmailRecipientTime city={prospect.city} state={prospect.state} />
          </div>
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setBlankOpen(false)} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="crm-email-blank-cancel">
              Cancel
            </Button>
            <Button
              onClick={sendBlank}
              disabled={sending || (!blankSubject.trim() && !blankBody.trim())}
              className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90"
              data-testid="crm-email-blank-launch"
            >
              {sending ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Send size={15} className="mr-1" />} Launch Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};


// ===========================================================================
// Prospect Detail Panel
// ===========================================================================
const ProspectDetail = ({ token, id, meta, assignees, isAdmin, onClose, onChanged, onOpenCandidate }) => {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [linked, setLinked] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [deal, setDeal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actType, setActType] = useState("Call");
  const [actNote, setActNote] = useState("");
  const [actOutcome, setActOutcome] = useState("none");
  const [actFollow, setActFollow] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [matches, setMatches] = useState(null);
  const [matchBusy, setMatchBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await crmGetProspect(token, id);
      setP(d.prospect);
      setLinked(d.linked_assessment);
      setAssessment(d.assessment || null);
      setDeal(d.deal || null);
      setNoteDraft(d.prospect.notes || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load prospect");
      onClose();
    }
  }, [token, id, onClose]);

  useEffect(() => { load(); }, [load]);

  const patch = async (body) => {
    setBusy(true);
    try {
      const res = await crmUpdateProspect(token, id, body);
      setP(res.prospect);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const logActivity = async (typeOverride) => {
    const type = typeOverride || actType;
    setBusy(true);
    try {
      const res = await crmLogActivity(token, id, {
        activity_type: type,
        note: actNote.trim() || null,
        outcome: actOutcome === "none" ? null : actOutcome,
        next_follow_up_date: actFollow || null,
      });
      setP(res.prospect);
      setActNote(""); setActFollow(""); setActOutcome("none");
      toast.success(`${type} logged`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log activity");
    } finally {
      setBusy(false);
    }
  };

  // Save any pending edits (currently the Lead Notes draft), then close the
  // workspace. All other controls auto-save on change.
  const saveAndClose = async () => {
    setBusy(true);
    try {
      if ((noteDraft || "") !== (p?.notes || "")) {
        await crmUpdateProspect(token, id, { notes: noteDraft });
      }
      onChanged && onChanged();
      toast.success("Prospect updated");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      setBusy(false);
    }
  };

  const convert = async () => {
    setBusy(true);
    try {
      const res = await crmConvert(token, id, {});
      setP(res.prospect);
      toast.success("Converted to opportunity");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally {
      setBusy(false);
    }
  };

  const submissionId = assessment?.submission_id || p?.assessment_submission_id;
  const hasAssessment = !!submissionId;
  // Assessment/blueprint + candidate matching are lead/talent features — hidden
  // from CRM-only Sales Users (backend also enforces via leads.view/matches.view).
  const canSeeAssessment = hasPerm("leads.view");
  const canSeeMatches = hasPerm("matches.view");

  // Blueprint-driven candidate matching (Phase 3).
  useEffect(() => {
    if (!submissionId || !canSeeMatches) { setMatches(null); return; }
    setMatchBusy(true);
    leadRecommendedCandidates(token, submissionId)
      .then((r) => setMatches(r.candidates || []))
      .catch(() => setMatches([]))
      .finally(() => setMatchBusy(false));
  }, [submissionId, token, canSeeMatches]);

  const canGenerate = p && p.status === "Qualified" && !hasAssessment;
  const openBlueprint = () => submissionId && window.open(`/blueprint/${submissionId}`, "_blank");
  const runAssessment = () => navigate(`/crm/assessment/${id}`);

  return (
    <WorkspaceShell
      onClose={onClose}
      loading={!p}
      ariaLabel={p ? `${p.name} — Prospect workspace` : "Prospect workspace"}
      testid="crm-detail-panel"
      closeTestid="crm-workspace-close-x"
      header={p && (
        <WorkspaceHeader
          title={p.name}
          badges={
            <>
              <Badge className={`border ${statusColor(p.status)}`}>{p.status}</Badge>
              {p.opportunity_created && (
                <Badge className="bg-[#2EE59D]/15 text-[#2EE59D] border border-[#2EE59D]/30">
                  <TrendingUp size={12} className="mr-1" /> Opportunity
                </Badge>
              )}
              {p.marketing_status && p.marketing_status !== "Subscribed" && (
                <Badge className="bg-[#F5B94A]/15 text-[#F5B94A] border border-[#F5B94A]/30" data-testid="crm-marketing-status">
                  <Ban size={12} className="mr-1" /> {p.marketing_status}
                </Badge>
              )}
            </>
          }
          subtitle={`${p.job_title || "—"}${p.company_name ? ` · ${p.company_name}` : ""}${p.industry ? ` · ${p.industry}` : ""}`}
          actions={
            <>
              <QuickLink href={p.corporate_phone ? `tel:${p.corporate_phone}` : null} icon={Phone} label="Call" testid="crm-quick-call" disabledTip="No phone number available." />
              <ProspectEmailAction token={token} prospect={p} onSent={load} />
              <QuickLink
                icon={Copy}
                label="Copy Email"
                testid="crm-quick-copy-email"
                disabledTip="No email address available."
                onClick={p.email ? (async () => {
                  try {
                    if (navigator.clipboard?.writeText) {
                      await navigator.clipboard.writeText(p.email);
                    } else {
                      const ta = document.createElement("textarea");
                      ta.value = p.email; ta.style.position = "fixed"; ta.style.opacity = "0";
                      document.body.appendChild(ta); ta.select();
                      document.execCommand("copy"); document.body.removeChild(ta);
                    }
                    toast.success(`Copied ${p.email}`);
                  } catch {
                    toast.error("Could not copy email");
                  }
                }) : undefined}
              />
              <QuickLink href={normalizeUrl(p.website)} icon={ExternalLink} label="Website" testid="crm-quick-website" external disabledTip="No website available." />
              <QuickLink href={normalizeUrl(p.linkedin_url)} icon={Linkedin} label="LinkedIn" testid="crm-quick-linkedin" external disabledTip="No LinkedIn profile available." />
              {hasPerm("suppressions.manage") && (
                <SuppressMarketingAction token={token} prospect={p} onDone={load} />
              )}
            </>
          }
        />
      )}
      footer={p && (
        <>
          <span className="text-white/35 text-xs mr-auto hidden sm:inline">
            Changes to controls save automatically.
          </span>
          <Button variant="ghost" className="text-white/60 hover:text-white" onClick={onClose} disabled={busy} data-testid="crm-workspace-cancel">
            Cancel
          </Button>
          <Button className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" onClick={saveAndClose} disabled={busy} data-testid="crm-workspace-update">
            {busy ? <Loader2 className="animate-spin mr-1" size={15} /> : <CheckCircle2 size={15} className="mr-1" />}
            Update
          </Button>
        </>
      )}
    >
      {p && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                {/* LEFT (2 cols): controls + activity */}
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <div className="text-white/70 text-sm font-medium mb-3">Sales Controls</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {isAdmin && (
                        <div>
                          <label className="text-white/45 text-xs">Assigned To</label>
                          <Select value={p.assigned_to || "unassigned"} onValueChange={(v) => patch({ assigned_to: v === "unassigned" ? null : v })}>
                            <SelectTrigger className={inputCls + " mt-1"} data-testid="crm-detail-assignee"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div>
                        <label className="text-white/45 text-xs">Status</label>
                        <Select value={p.status} onValueChange={(v) => patch({ status: v })}>
                          <SelectTrigger className={inputCls + " mt-1"} data-testid="crm-detail-status"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                            {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-white/45 text-xs">Outcome</label>
                        <Select value={p.outcome || "none"} onValueChange={(v) => patch({ outcome: v === "none" ? "" : v })}>
                          <SelectTrigger className={inputCls + " mt-1"} data-testid="crm-detail-outcome"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                            <SelectItem value="none">—</SelectItem>
                            {meta.outcomes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-white/45 text-xs">Next Follow-Up</label>
                        <div className="mt-1">
                          <DatePicker value={p.next_follow_up_date || ""} placeholder="Set date"
                            onChange={(v) => patch({ next_follow_up_date: v })} testid="crm-detail-followup" />
                        </div>
                      </div>
                      <div>
                        <label className="text-white/45 text-xs">Meeting Date</label>
                        <div className="mt-1">
                          <DatePicker value={p.meeting_date || ""} placeholder="Set date"
                            onChange={(v) => patch({ meeting_date: v })} testid="crm-detail-meeting" />
                        </div>
                      </div>
                      <div>
                        <label className="text-white/45 text-xs">Estimated Seats</label>
                        <Input type="number" className={inputCls + " mt-1"} value={p.estimated_seats ?? ""}
                          onChange={(e) => patch({ estimated_seats: e.target.value ? Number(e.target.value) : null })} data-testid="crm-detail-seats" />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button size="sm" variant="outline" className="border-white/15 text-white/80 h-8" onClick={() => logActivity("Call")} disabled={busy} data-testid="crm-action-log-call">
                        <Phone size={13} className="mr-1" /> Log Call
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/15 text-white/80 h-8" onClick={() => logActivity("Email")} disabled={busy} data-testid="crm-action-log-email">
                        <Mail size={13} className="mr-1" /> Log Email
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#2EE59D]/30 text-[#2EE59D] h-8" onClick={() => patch({ status: "Interested" })} disabled={busy} data-testid="crm-action-interested">
                        Mark Interested
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#2EE59D]/40 text-[#2EE59D] h-8" onClick={() => patch({ status: "Qualified" })} disabled={busy} data-testid="crm-action-qualified">
                        Mark Qualified
                      </Button>
                    </div>
                  </div>

                  <Separator className="bg-white/10" />

                  <div>
                    <div className="text-white/70 text-sm font-medium mb-3">Log Activity</div>
                    <div className="grid grid-cols-2 gap-3">
                      <Select value={actType} onValueChange={setActType}>
                        <SelectTrigger className={inputCls} data-testid="crm-activity-type"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                          {meta.activity_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={actOutcome} onValueChange={setActOutcome}>
                        <SelectTrigger className={inputCls} data-testid="crm-activity-outcome"><SelectValue placeholder="Outcome (optional)" /></SelectTrigger>
                        <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                          <SelectItem value="none">No outcome</SelectItem>
                          {meta.outcomes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea className="bg-[#0A0F1E] border-white/10 text-white mt-3" placeholder="Notes about this activity…"
                      value={actNote} onChange={(e) => setActNote(e.target.value)} data-testid="crm-activity-note" />
                    <div className="flex items-center gap-3 mt-3">
                      <div className="w-52">
                        <DatePicker value={actFollow} onChange={setActFollow} placeholder="Follow-up date"
                          testid="crm-activity-followup" />
                      </div>
                      <Button className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" onClick={() => logActivity()} disabled={busy} data-testid="crm-activity-submit">
                        {busy ? <Loader2 className="animate-spin" size={16} /> : "Log Activity"}
                      </Button>
                    </div>
                  </div>

                  {/* Email Engagement — prospect-level Opened summary */}
                  {(p.activity_log || []).some((a) => a.tracking_id) && (
                    <div data-testid="crm-email-engagement">
                      <div className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                        <MailOpen size={14} /> Email Engagement
                      </div>
                      <div className="rounded-lg border border-white/10 p-3 bg-[#0A0F1E] grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <div className="col-span-2 flex items-center gap-2">
                          <span className="text-white/45 text-xs">Status</span>
                          {p.email_opened ? (
                            <Badge className="border bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30" data-testid="crm-engagement-status">
                              <Eye size={11} className="mr-1" /> Opened
                            </Badge>
                          ) : p.email_automated_count ? (
                            <Badge className="border bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30" data-testid="crm-engagement-status">
                              Possible Automated Open
                            </Badge>
                          ) : (
                            <Badge className="border bg-white/5 text-white/50 border-white/15" data-testid="crm-engagement-status">
                              Not Opened
                            </Badge>
                          )}
                        </div>
                        {p.email_opened && (() => {
                          const lvl = engagementLevel(p);
                          const meta = ENGAGEMENT_META[lvl] || ENGAGEMENT_META.None;
                          return (
                            <div className="col-span-2 flex items-center gap-2">
                              <span className="text-white/45 text-xs">Engagement</span>
                              <Badge className={`border ${meta.cls}`} data-testid="crm-engagement-level">{lvl}</Badge>
                            </div>
                          );
                        })()}
                        <div>
                          <div className="text-white/40 text-xs">First Opened</div>
                          <div className="text-white/75">{fmtDateTime(p.email_first_opened_at)}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs">Last Opened</div>
                          <div className="text-white/75">{fmtDateTime(p.email_last_opened_at)}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs">Open Count</div>
                          <div className="text-white/75" data-testid="crm-engagement-count">{p.email_open_count || 0}</div>
                        </div>
                        {!!p.email_automated_count && (
                          <div>
                            <div className="text-white/40 text-xs">Automated Loads</div>
                            <div className="text-white/55" data-testid="crm-engagement-automated" title="Image-proxy pre-cache, privacy preload, or security scanner — not a confirmed human open">
                              {p.email_automated_count}
                            </div>
                          </div>
                        )}
                      </div>
                      {!p.email_opened && !!p.email_automated_count && (
                        <p className="text-white/35 text-[11px] mt-1.5">
                          Only automated image loads detected so far (proxy/privacy/scanner) — not a confirmed human open.
                        </p>
                      )}
                    </div>
                  )}

                  {/* TeamBuilder Activity — funnel from tracked assessment links */}
                  {(p.tb_visited || p.tb_started || p.tb_submitted) && (
                    <div data-testid="crm-tb-activity">
                      <div className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                        <Rocket size={14} /> TeamBuilder Activity
                      </div>
                      <div className="rounded-lg border border-white/10 p-3 bg-[#0A0F1E] space-y-3">
                        {/* Funnel status badges */}
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            className={`border ${p.tb_visited ? "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30" : "bg-white/5 text-white/40 border-white/15"}`}
                            data-testid="crm-tb-visited">
                            <MousePointerClick size={11} className="mr-1" /> Visited
                          </Badge>
                          <span className="text-white/20">→</span>
                          <Badge
                            className={`border ${p.tb_started ? "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30" : "bg-white/5 text-white/40 border-white/15"}`}
                            data-testid="crm-tb-started">
                            <PenLine size={11} className="mr-1" /> Started
                          </Badge>
                          <span className="text-white/20">→</span>
                          <Badge
                            className={`border ${p.tb_submitted ? "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30" : "bg-white/5 text-white/40 border-white/15"}`}
                            data-testid="crm-tb-submitted">
                            <ClipboardCheck size={11} className="mr-1" /> Submitted
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div>
                            <div className="text-white/40 text-xs">First Visit</div>
                            <div className="text-white/75">{fmtDateTime(p.tb_first_visit)}</div>
                          </div>
                          <div>
                            <div className="text-white/40 text-xs">Last Visit</div>
                            <div className="text-white/75">{fmtDateTime(p.tb_last_visit)}</div>
                          </div>
                          <div>
                            <div className="text-white/40 text-xs">Visit Count</div>
                            <div className="text-white/75" data-testid="crm-tb-visit-count">{p.tb_visit_count || 0}</div>
                          </div>
                          {p.tb_started && (
                            <div>
                              <div className="text-white/40 text-xs">Started</div>
                              <div className="text-white/75">{fmtDateTime(p.tb_started_at)}</div>
                            </div>
                          )}
                          {p.tb_submitted && (
                            <div>
                              <div className="text-white/40 text-xs">Submitted</div>
                              <div className="text-white/75">{fmtDateTime(p.tb_submitted_at)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                      <Clock size={14} /> Activity Timeline ({p.activity_count || 0})
                    </div>
                    {(p.activity_log || []).length === 0 ? (
                      <div className="text-white/35 text-sm">No activity yet.</div>
                    ) : (
                      <div className="space-y-2">
                        {p.activity_log.map((a) => {
                          const isEmail = a.channel === "email" || !!a.tracking_id;
                          const isTb = a.channel === "teambuilder";
                          const opens = a.open_count || 0;
                          const autos = a.automated_count || 0;
                          const tbMeta = {
                            visit: { cls: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30", Icon: MousePointerClick, label: "Visited TeamBuilder" },
                            start: { cls: "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30", Icon: PenLine, label: "Started Assessment" },
                            submit: { cls: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30", Icon: ClipboardCheck, label: "Submitted Assessment" },
                          }[a.tb_kind] || { cls: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30", Icon: Rocket, label: a.note || "TeamBuilder" };
                          if (isTb) {
                            const TbIcon = tbMeta.Icon;
                            return (
                              <div key={a.id} className="rounded-lg border border-white/10 p-3 text-sm bg-[#0A0F1E]" data-testid="crm-activity-item">
                                <div className="flex items-center justify-between">
                                  <span className="text-white/80 font-medium flex items-center gap-1.5">
                                    <TbIcon size={13} className="text-white/50" /> TeamBuilder
                                  </span>
                                  <Badge className={`border text-[10px] ${tbMeta.cls}`}>{tbMeta.label}</Badge>
                                </div>
                                <div className="text-white/50 text-xs mt-1">{fmtDateTime(a.timestamp)}</div>
                              </div>
                            );
                          }
                          const badge = opens > 0
                            ? { cls: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30", label: "Opened" }
                            : autos > 0
                              ? { cls: "bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30", label: "Automated" }
                              : { cls: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30", label: "Draft Launched" };
                          return (
                            <div key={a.id} className="rounded-lg border border-white/10 p-3 text-sm bg-[#0A0F1E]" data-testid="crm-activity-item">
                              <div className="flex items-center justify-between">
                                <span className="text-white/80 font-medium">
                                  {isEmail
                                    ? `Email${a.template_name ? ` — ${a.template_name}` : ""}`
                                    : `${a.type}${a.outcome ? ` · ${a.outcome}` : ""}`}
                                </span>
                                {isEmail && (
                                  <Badge className={`border text-[10px] ${badge.cls}`}>{badge.label}</Badge>
                                )}
                              </div>
                              {isEmail ? (
                                <div className="mt-1 space-y-0.5">
                                  {a.subject && (
                                    <div className="text-white/70 text-xs" data-testid="crm-activity-subject">
                                      Subject: {a.subject}
                                    </div>
                                  )}
                                  {a.recipient && (
                                    <div className="text-white/45 text-xs" data-testid="crm-activity-recipient">
                                      To: {a.recipient}
                                    </div>
                                  )}
                                  <div className="text-white/50 text-xs">Draft launched {fmtDateTime(a.initiated_at || a.sent_at || a.timestamp)}</div>
                                  {opens > 0 && (
                                    <div className="text-[#2EE59D] text-xs" data-testid="crm-activity-opened">
                                      Opened {fmtDateTime(a.opened_at)} &bull; last {fmtDateTime(a.last_opened_at || a.opened_at)} &bull; {opens} open{opens === 1 ? "" : "s"}
                                    </div>
                                  )}
                                  {opens === 0 && autos > 0 && (
                                    <div className="text-[#F5B94A] text-xs" data-testid="crm-activity-automated">
                                      Automated image load {fmtDateTime(a.last_automated_at)} &bull; {autos} event{autos === 1 ? "" : "s"} (not a confirmed open)
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  {a.note && <div className="text-white/55 text-xs mt-1">{a.note}</div>}
                                </>
                              )}
                              <div className="text-white/30 text-xs mt-1">by {a.by_name}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT (1 col): notes + assessment/deal + matches + company info */}
                <div className="space-y-6">
                  {/* Lead Notes — placed first so keyboard focus flows here
                      right after the Log Activity section (matches desired order). */}
                  <div>
                    <div className="text-white/70 text-sm font-medium mb-2">Lead Notes</div>
                    <Textarea className="bg-[#0A0F1E] border-white/10 text-white" rows={4}
                      value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} data-testid="crm-notes" />
                    <Button size="sm" variant="outline" className="border-white/15 text-white/80 mt-2 h-8"
                      onClick={() => patch({ notes: noteDraft })} disabled={busy} data-testid="crm-notes-save">
                      Save Notes
                    </Button>
                  </div>

                  {/* TeamBuilder Assessment / Deal */}
                  {canSeeAssessment && (
                  <div className="rounded-xl border border-[#1E6BFF]/30 bg-[#1E6BFF]/10 p-4" data-testid="crm-assessment-section">
                    <div className="flex items-center gap-2 text-white font-medium mb-2">
                      <Sparkles size={16} className="text-[#66B2FF]" /> TeamBuilder Assessment
                    </div>
                    {hasAssessment ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="rounded-lg border border-white/10 p-3 bg-[#0A0F1E]">
                            <div className="text-white/45 text-xs">Est. Monthly</div>
                            <div className="text-lg font-bold text-[#2EE59D]">${(deal?.monthly_value ?? assessment?.monthly_value ?? 0).toLocaleString()}</div>
                          </div>
                          <div className="rounded-lg border border-white/10 p-3 bg-[#0A0F1E]">
                            <div className="text-white/45 text-xs">Est. Annual</div>
                            <div className="text-lg font-bold text-white">${(deal?.annual_value ?? assessment?.annual_value ?? 0).toLocaleString()}</div>
                          </div>
                          <div className="rounded-lg border border-white/10 p-3 bg-[#0A0F1E]">
                            <div className="text-white/45 text-xs">Team Size</div>
                            <div className="text-lg font-bold text-white">{deal?.team_size ?? assessment?.team_size ?? "—"}</div>
                          </div>
                          <div className="rounded-lg border border-white/10 p-3 bg-[#0A0F1E]">
                            <div className="text-white/45 text-xs">Timeline</div>
                            <div className="text-sm font-medium text-white">{assessment?.timeline || deal?.timeline || "—"}</div>
                          </div>
                        </div>
                        {(assessment?.roles || deal?.roles || []).length > 0 && (
                          <div>
                            <div className="text-white/45 text-xs mb-1">Recommended Roles</div>
                            <div className="flex flex-wrap gap-1.5">
                              {(assessment?.roles || deal?.roles).map((r, i) => (
                                <Badge key={i} className="bg-white/10 text-white/80 border border-white/15">
                                  {r.name}{r.quantity ? ` ×${r.quantity}` : ""}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button size="sm" className="bg-[#1E6BFF] hover:bg-[#3B82F6] h-8" onClick={openBlueprint} data-testid="crm-view-blueprint">
                            <FileText size={13} className="mr-1" /> View Blueprint
                          </Button>
                          <Button size="sm" variant="outline" className="border-white/15 text-white/80 h-8" onClick={openBlueprint} data-testid="crm-view-assessment">
                            View Assessment
                          </Button>
                          <Button size="sm" variant="outline" className="border-white/15 text-white/80 h-8" onClick={runAssessment} data-testid="crm-regenerate-blueprint">
                            Regenerate
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-white/60 text-sm mb-3">
                          {canGenerate
                            ? "Launch the guided TeamBuilder Assessment with this prospect prefilled — conduct discovery live and generate their Blueprint & Deal."
                            : "Mark this prospect Qualified to launch the TeamBuilder Assessment."}
                        </p>
                        <Button
                          className="w-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white"
                          disabled={!canGenerate}
                          onClick={runAssessment}
                          data-testid="crm-generate-assessment"
                        >
                          <Sparkles size={15} className="mr-1.5" /> Generate TeamBuilder Assessment
                        </Button>
                      </div>
                    )}
                  </div>
                  )}

                  {/* Top Candidate Matches (vs Blueprint) */}
                  {hasAssessment && canSeeMatches && (
                    <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]" data-testid="crm-candidate-matches">
                      <div className="flex items-center gap-2 text-white/70 text-sm font-medium mb-3">
                        <Users size={14} /> Top Candidate Matches
                        {matchBusy && <Loader2 size={13} className="animate-spin text-white/40" />}
                      </div>
                      {!matchBusy && (matches || []).length === 0 ? (
                        <div className="text-white/35 text-sm">No candidate matches yet. Add candidates to your talent pool to see Blueprint-based matches.</div>
                      ) : (
                        <div className="space-y-2">
                          {(matches || []).slice(0, 5).map((m) => (
                            <div key={m.candidate_id} role="button" tabIndex={0}
                              aria-label={`Candidate ${m.name}, ${Math.round(m.overall || 0)} percent match. Open candidate workspace.`}
                              onClick={() => onOpenCandidate && onOpenCandidate(m.candidate_id)}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); } }}
                              className="rounded-lg border border-white/10 p-3 bg-[#0E1424] cursor-pointer transition-colors hover:bg-[#141c33] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66B2FF] focus-visible:ring-offset-1 focus-visible:ring-offset-[#0A0F1E]"
                              data-testid="crm-match-row">
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-white/85 text-sm font-medium truncate">{m.name}</div>
                                  <div className="text-white/40 text-xs truncate">{m.current_position || "—"}</div>
                                </div>
                                <Badge className="bg-[#2EE59D]/15 text-[#2EE59D] border border-[#2EE59D]/30 tabular-nums">
                                  {Math.round(m.overall || 0)}%
                                </Badge>
                              </div>
                              {(m.strengths || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {m.strengths.slice(0, 3).map((s, i) => (
                                    <span key={i} className="text-[10px] rounded bg-white/5 border border-white/10 px-1.5 py-0.5 text-white/60">{s}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Company / contact info */}
                  <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]">
                    <div className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                      <Building2 size={14} /> Company Information
                    </div>
                    <div className="space-y-2">
                      <DetailField label="Email" value={p.email} icon={Mail} />
                      <DetailField label="Phone" value={p.corporate_phone} icon={Phone} />
                      <DetailField label="Company" value={p.company_name} icon={Building2} />
                      <DetailField label="Industry" value={p.industry} icon={Target} />
                      <DetailField label="Employees" value={p.employee_count} icon={Users} />
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="flex items-center gap-2 text-white/45 text-sm"><ListFilter size={13} /> Campaign</span>
                        <Select value={p.campaign || "none"} disabled={busy}
                          onValueChange={(v) => patch({ campaign: v === "none" ? "" : v })}>
                          <SelectTrigger className="h-8 w-44 bg-[#0F1424] border-white/10 text-white text-sm" data-testid="crm-ws-campaign">
                            <SelectValue placeholder="No campaign" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                            <SelectItem value="none">No campaign</SelectItem>
                            {Array.from(new Set([...(meta?.active_campaigns || []), ...(p.campaign ? [p.campaign] : [])]))
                              .sort().map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <DetailField label="Source" value={p.source} />
                      <DetailField label="Assigned" value={p.assigned_to_name} icon={UserCheck} />
                      <DetailField label="Address" value={p.company_address} />
                      <DetailField label="City" value={p.city} />
                      <DetailField label="State" value={p.state} />
                      <ProspectLocalTime city={p.city} state={p.state} />
                      <DetailField label="Last Activity" value={fmtDate(p.last_activity_date)} icon={Clock} />
                    </div>
                  </div>
                </div>
              </div>
      )}
    </WorkspaceShell>
  );
};

// ===========================================================================
// Dashboard
// ===========================================================================
const StatCard = ({ label, value, color = "text-white", onClick, icon: Icon, testid }) => {
  const clickable = typeof onClick === "function";
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      className={`rounded-xl border border-white/10 p-4 bg-[#0A0F1E] transition-colors ${clickable ? "cursor-pointer hover:border-[#1E6BFF]/50 hover:bg-white/5" : ""}`}
    >
      <div className={`text-3xl font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-white/45 text-sm mt-1 flex items-center gap-1.5">
        {Icon && <Icon size={13} />}{label}
      </div>
    </div>
  );
};

const DistList = ({ title, rows, icon: Icon }) => (
  <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]">
    <div className="flex items-center gap-2 text-white/70 text-sm font-medium mb-3">
      {Icon && <Icon size={15} />} {title}
    </div>
    {rows.length === 0 ? (
      <div className="text-white/35 text-sm">No data yet</div>
    ) : (
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="text-white/70 truncate">{r.label}</span>
            <span className="text-white/85 tabular-nums">{r.count}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const PROPOSAL_STATUSES = ["Draft", "Sent", "Won", "Lost"];
const proposalColor = (s) => ({
  Draft: "bg-white/10 text-white/70 border-white/15",
  Sent: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30",
  Won: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30",
  Lost: "bg-[#FF5C7A]/15 text-[#FF5C7A] border-[#FF5C7A]/30",
}[s] || "bg-white/10 text-white/70 border-white/15");

// Inline proposal-status control on a deal row. Stops row-click propagation so
// changing status does not also open the prospect workspace.
const DealStatusControl = ({ deal, token, canManage, onUpdated }) => {
  const [saving, setSaving] = useState(false);
  const current = deal.proposal_status || "Draft";
  if (!canManage) {
    return <Badge className={`border ${proposalColor(current)}`}>{current}</Badge>;
  }
  const change = async (next) => {
    if (next === current) return;
    setSaving(true);
    try {
      const res = await crmUpdateDeal(token, deal.id, { proposal_status: next });
      toast.success(`Deal marked ${next}`);
      onUpdated && onUpdated(res.deal);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update deal");
    } finally {
      setSaving(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          disabled={saving}
          data-testid="crm-deal-status-trigger"
          className="inline-flex items-center gap-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6BFF]/60">
          <Badge className={`border ${proposalColor(current)} cursor-pointer hover:opacity-80`}>
            {saving ? <Loader2 size={11} className="animate-spin" /> : current}
            <ChevronDown size={11} className="ml-1 opacity-60" />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0E1424] border-white/10" onClick={(e) => e.stopPropagation()}>
        {PROPOSAL_STATUSES.map((s) => (
          <DropdownMenuItem
            key={s}
            data-testid={`crm-deal-status-opt-${s.toLowerCase()}`}
            onClick={(e) => { e.stopPropagation(); change(s); }}
            className="text-white/80 focus:bg-white/10 cursor-pointer">
            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${proposalColor(s).split(" ")[0]}`} />
            {s}{s === current ? " ✓" : ""}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const CrmDeals = ({ token, onOpenProspect }) => {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("monthly_desc");
  const canManage = hasPerm("crm.manage");

  useEffect(() => {
    crmDeals(token).then(setData).catch(() => toast.error("Failed to load deals"));
  }, [token]);

  const applyDealUpdate = useCallback((updated) => {
    if (!updated) return;
    setData((prev) => prev
      ? { ...prev, deals: prev.deals.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)) }
      : prev);
  }, []);

  const assignees = useMemo(() => {
    const set = new Set((data?.deals || []).map((d) => d.assigned_to_name).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  const visibleDeals = useMemo(() => {
    let rows = [...(data?.deals || [])];
    if (statusFilter !== "all") {
      rows = rows.filter((d) => (d.proposal_status || "Draft") === statusFilter);
    }
    if (assigneeFilter !== "all") {
      rows = rows.filter((d) => (d.assigned_to_name || "Unassigned") === assigneeFilter);
    }
    const statusOrder = { Draft: 0, Sent: 1, Won: 2, Lost: 3 };
    const cmp = {
      monthly_desc: (a, b) => (b.monthly_value || 0) - (a.monthly_value || 0),
      monthly_asc: (a, b) => (a.monthly_value || 0) - (b.monthly_value || 0),
      annual_desc: (a, b) => (b.annual_value || 0) - (a.annual_value || 0),
      annual_asc: (a, b) => (a.annual_value || 0) - (b.annual_value || 0),
      status: (a, b) => (statusOrder[a.proposal_status || "Draft"] ?? 0) - (statusOrder[b.proposal_status || "Draft"] ?? 0),
      company: (a, b) => (a.company || "").localeCompare(b.company || ""),
    }[sortBy];
    if (cmp) rows.sort(cmp);
    return rows;
  }, [data, statusFilter, assigneeFilter, sortBy]);

  if (!data) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-white/50" /></div>;
  const money = (v) => `$${(v || 0).toLocaleString()}`;
  const filtMonthly = visibleDeals.reduce((s, d) => s + (d.monthly_value || 0), 0);
  const filtAnnual = visibleDeals.reduce((s, d) => s + (d.annual_value || 0), 0);

  return (
    <div className="space-y-5" data-testid="crm-deals">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Total Deals" value={data.count} />
        <StatCard label="Pipeline (Monthly)" value={money(data.total_monthly)} color="text-[#2EE59D]" />
        <StatCard label="Pipeline (Annual)" value={money(data.total_annual)} color="text-[#66B2FF]" />
      </div>

      {/* Filters + sorting */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center">
        <div className="flex items-center gap-2 text-white/50 text-sm">
          <Filter size={14} /> Filters
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="crm-deals-filter-status" className="bg-[#0F1424] border-white/10 h-10 text-white w-full sm:w-40">
            <SelectValue placeholder="Proposal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All proposals</SelectItem>
            {PROPOSAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger data-testid="crm-deals-filter-assignee" className="bg-[#0F1424] border-white/10 h-10 text-white w-full sm:w-48">
            <SelectValue placeholder="Assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            {assignees.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="crm-deals-sort" className="bg-[#0F1424] border-white/10 h-10 text-white w-full sm:w-52">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly_desc">Monthly (high → low)</SelectItem>
            <SelectItem value="monthly_asc">Monthly (low → high)</SelectItem>
            <SelectItem value="annual_desc">Annual (high → low)</SelectItem>
            <SelectItem value="annual_asc">Annual (low → high)</SelectItem>
            <SelectItem value="status">Proposal status</SelectItem>
            <SelectItem value="company">Company (A → Z)</SelectItem>
          </SelectContent>
        </Select>
        {(statusFilter !== "all" || assigneeFilter !== "all") && (
          <div className="text-white/40 text-xs" data-testid="crm-deals-filtered-summary">
            {visibleDeals.length} shown · {money(filtMonthly)}/mo · {money(filtAnnual)}/yr
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/50">Company</TableHead>
              <TableHead className="text-white/50">Contact</TableHead>
              <TableHead className="text-white/50">Monthly</TableHead>
              <TableHead className="text-white/50">Annual</TableHead>
              <TableHead className="text-white/50">Team</TableHead>
              <TableHead className="text-white/50">Timeline</TableHead>
              <TableHead className="text-white/50">Assigned</TableHead>
              <TableHead className="text-white/50">Proposal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleDeals.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-white/40">
                {data.deals.length === 0
                  ? "No deals yet. Generate a TeamBuilder Assessment from a Qualified prospect to create one."
                  : "No deals match the current filters."}
              </TableCell></TableRow>
            ) : visibleDeals.map((d) => (
              <TableRow key={d.id} className="border-white/10 hover:bg-white/5 cursor-pointer" data-testid="crm-deal-row"
                onClick={() => onOpenProspect(d.prospect_id)}>
                <TableCell className="text-white font-medium">{d.company || "—"}</TableCell>
                <TableCell className="text-white/70">{d.contact || "—"}</TableCell>
                <TableCell className="text-[#2EE59D] tabular-nums">{money(d.monthly_value)}</TableCell>
                <TableCell className="text-white/80 tabular-nums">{money(d.annual_value)}</TableCell>
                <TableCell className="text-white/60">{d.team_size ?? "—"}</TableCell>
                <TableCell className="text-white/60 text-sm">{d.timeline || "—"}</TableCell>
                <TableCell className="text-white/60 text-sm">{d.assigned_to_name || "—"}</TableCell>
                <TableCell>
                  <DealStatusControl deal={d} token={token} canManage={canManage} onUpdated={applyDealUpdate} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const CampaignFunnelView = ({ token, onTbFilter }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    crmCampaignFunnel(token).then(setData).catch(() => setData({ campaigns: [] }));
  }, [token]);
  return (
    <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]" data-testid="crm-campaign-funnel">
      <div className="flex items-center gap-2 text-white/70 text-sm font-medium mb-3">
        <Rocket size={15} /> Campaign Funnel — which outreach converts best
      </div>
      {!data ? (
        <div className="py-8 text-center"><Loader2 className="animate-spin mx-auto text-white/40" size={18} /></div>
      ) : data.campaigns.length === 0 ? (
        <div className="text-white/35 text-sm">No campaign activity yet.</div>
      ) : (
        <div className="rounded-lg border border-white/10 overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50">Campaign</TableHead>
                <TableHead className="text-white/50 text-right">Prospects</TableHead>
                <TableHead className="text-white/50 text-right">Opened</TableHead>
                <TableHead className="text-white/50 text-right">Visits</TableHead>
                <TableHead className="text-white/50 text-right">Starts</TableHead>
                <TableHead className="text-white/50 text-right">Submissions</TableHead>
                <TableHead className="text-white/50 text-right">Submit Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.campaigns.map((c) => (
                <TableRow key={c.campaign} className="border-white/10 hover:bg-white/5" data-testid="crm-campaign-funnel-row">
                  <TableCell className="text-white font-medium">{c.campaign}</TableCell>
                  <TableCell className="text-white/70 tabular-nums text-right">{c.total}</TableCell>
                  <TableCell className="text-[#2EE59D] tabular-nums text-right">{c.opened}</TableCell>
                  <TableCell className="text-[#66B2FF] tabular-nums text-right">{c.visited}</TableCell>
                  <TableCell className="text-[#A78BFA] tabular-nums text-right">{c.started}</TableCell>
                  <TableCell className="text-[#2EE59D] tabular-nums text-right">{c.submitted}</TableCell>
                  <TableCell className="text-right">
                    <span className="text-white font-medium tabular-nums">{c.rates.submitted}%</span>
                    <span className="text-white/40 text-xs ml-1">of {c.total}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const CrmDashboard = ({ token, onOpenProspect, onOpenFilter, onEngagedFilter, onTbFilter }) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    crmStats(token).then(setStats).catch(() => toast.error("Failed to load dashboard"));
  }, [token]);

  if (!stats) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-white/50" /></div>;
  const sd = stats.status_dist || {};
  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
        <StatCard label="Total Prospects" value={stats.total} />
        <StatCard label="New" value={sd.New || 0} />
        <StatCard label="Attempted" value={sd.Attempted || 0} color="text-[#F5B94A]" />
        <StatCard label="Opened" value={stats.opened_prospects || 0} color="text-[#2EE59D]"
          icon={MailOpen} onClick={onOpenFilter} testid="crm-kpi-opened" />
        <StatCard label="Engaged" value={stats.engaged_prospects || 0} color="text-[#A78BFA]"
          icon={Eye} onClick={onEngagedFilter} testid="crm-kpi-engaged" />
        <StatCard label="Contacted" value={sd.Contacted || 0} color="text-[#66B2FF]" />
        <StatCard label="Interested" value={sd.Interested || 0} color="text-[#2EE59D]" />
        <StatCard label="Qualified" value={sd.Qualified || 0} color="text-[#2EE59D]" />
        <StatCard label="Meetings" value={stats.meetings_booked || 0} color="text-[#A78BFA]" />
      </div>

      {/* TeamBuilder Engagement Funnel — Opened → Visit → Start → Submission */}
      <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]" data-testid="crm-tb-funnel">
        <div className="flex items-center gap-2 text-white/70 text-sm font-medium mb-4">
          <Rocket size={15} /> TeamBuilder Engagement Funnel
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(() => {
            const conv = stats.tb_conversions || {};
            const stages = [
              { key: "opened", label: "Opened", value: stats.opened_prospects || 0, color: "text-[#2EE59D]", Icon: MailOpen, rate: null, filter: () => onOpenFilter && onOpenFilter() },
              { key: "visited", label: "TeamBuilder Visits", value: stats.tb_visitors || 0, color: "text-[#66B2FF]", Icon: MousePointerClick, rate: conv.opened_to_visit, rateLabel: "of Opened", filter: () => onTbFilter && onTbFilter("visited") },
              { key: "started", label: "Assessment Starts", value: stats.tb_starts || 0, color: "text-[#A78BFA]", Icon: PenLine, rate: conv.visit_to_start, rateLabel: "of Visits", filter: () => onTbFilter && onTbFilter("started") },
              { key: "submitted", label: "Submissions", value: stats.tb_submissions || 0, color: "text-[#2EE59D]", Icon: ClipboardCheck, rate: conv.start_to_submit, rateLabel: "of Starts", filter: () => onTbFilter && onTbFilter("submitted") },
            ];
            return stages.map((s, i) => {
              const Icon = s.Icon;
              return (
                <button
                  key={s.key}
                  onClick={s.filter}
                  className="text-left rounded-lg border border-white/10 bg-[#0E1424] p-3 hover:bg-white/5 transition-colors relative"
                  data-testid={`crm-tb-funnel-${s.key}`}>
                  <div className="flex items-center gap-2 text-white/45 text-xs mb-1">
                    <Icon size={13} className={s.color} /> {s.label}
                  </div>
                  <div className={`text-2xl font-semibold ${s.color}`}>{s.value}</div>
                  {i > 0 && (
                    <div className="text-white/40 text-[11px] mt-1">
                      <span className="text-white/70 font-medium">{s.rate ?? 0}%</span> {s.rateLabel}
                    </div>
                  )}
                </button>
              );
            });
          })()}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <DistList title="By Assigned User" rows={stats.by_user} icon={UserCheck} />
        <DistList title="By Campaign" rows={stats.by_campaign} icon={ListFilter} />
        <DistList title="By Industry" rows={stats.by_industry} icon={Building2} />
      </div>

      <CampaignFunnelView token={token} onTbFilter={onTbFilter} />

      <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]" data-testid="crm-followups-due">
        <div className="flex items-center gap-2 text-white/70 text-sm font-medium mb-3">
          <Calendar size={15} /> Follow-Ups Due ({(stats.follow_ups_due || []).length})
        </div>
        {(stats.follow_ups_due || []).length === 0 ? (
          <div className="text-white/35 text-sm">No follow-ups due. You're all caught up.</div>
        ) : (
          <div className="divide-y divide-white/10">
            {stats.follow_ups_due.map((f) => {
              const overdue = f.next_follow_up_date < today();
              return (
                <button key={f.id} onClick={() => onOpenProspect(f.id)}
                  className="w-full flex items-center justify-between py-2.5 text-left hover:bg-white/5 rounded-md px-2 -mx-2 transition-colors"
                  data-testid="crm-followup-item">
                  <div>
                    <div className="text-white/85 text-sm">{f.name} <span className="text-white/40">· {f.company_name || "—"}</span></div>
                    <div className="text-white/40 text-xs">{f.assigned_to_name || "Unassigned"}</div>
                  </div>
                  <Badge className={`border ${overdue ? "bg-[#FF5C7A]/15 text-[#FF5C7A] border-[#FF5C7A]/30" : "bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30"}`}>
                    {overdue ? "Overdue" : "Today"} · {fmtDate(f.next_follow_up_date)}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// Create Prospect Modal — add a single prospect on the fly
// ===========================================================================
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="text-white/60 text-xs font-medium">{label}</label>
    {children}
  </div>
);

const CreateProspectModal = ({ token, meta, assignees, isAdmin, onClose, onCreated }) => {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", job_title: "", company_name: "",
    corporate_phone: "", industry: "", website: "", linkedin_url: "",
    city: "", state: "",
    employee_count: "", estimated_seats: "", status: meta?.statuses?.[0] || "New",
    assigned_to: "unassigned", campaign: "", notes: "", next_follow_up_date: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailValid = EMAIL_RE.test(form.email.trim());
  const canSave = emailValid && !busy;

  const submit = async () => {
    if (!emailValid) { toast.error("Please enter a valid email address"); return; }
    setBusy(true);
    try {
      const body = {
        first_name: form.first_name.trim() || null,
        last_name: form.last_name.trim() || null,
        email: form.email.trim(),
        job_title: form.job_title.trim() || null,
        company_name: form.company_name.trim() || null,
        corporate_phone: form.corporate_phone.trim() || null,
        industry: form.industry.trim() || null,
        website: form.website.trim() || null,
        linkedin_url: form.linkedin_url.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        employee_count: form.employee_count ? Number(form.employee_count) : null,
        estimated_seats: form.estimated_seats ? Number(form.estimated_seats) : null,
        status: form.status,
        campaign: form.campaign.trim() || null,
        notes: form.notes.trim() || null,
        next_follow_up_date: form.next_follow_up_date || null,
        source: "Manual",
      };
      if (isAdmin && form.assigned_to !== "unassigned") body.assigned_to = form.assigned_to;
      const res = await crmCreateProspect(token, body);
      toast.success("Prospect created");
      onCreated?.(res?.prospect?.id);
      onClose();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("A prospect with this email already exists");
      } else {
        toast.error(e?.response?.data?.detail || "Failed to create prospect");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0E1424] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="crm-create-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Plus size={18} className="text-[#66B2FF]" /> New Prospect
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <Field label="First Name">
            <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
              className={inputCls} placeholder="Jane" data-testid="crm-create-first-name" />
          </Field>
          <Field label="Last Name">
            <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
              className={inputCls} placeholder="Doe" data-testid="crm-create-last-name" />
          </Field>

          <div className="space-y-1.5 sm:col-span-2">
            <label className="text-white/60 text-xs font-medium">Email <span className="text-[#FF5C7A]">*</span></label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} type="email"
              className={`${inputCls} ${form.email && !emailValid ? "border-[#FF5C7A]/60" : ""}`}
              placeholder="jane@company.com" data-testid="crm-create-email" />
            {form.email && !emailValid && (
              <p className="text-[#FF5C7A] text-xs">Enter a valid email address.</p>
            )}
          </div>

          <Field label="Job Title">
            <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)}
              className={inputCls} placeholder="VP of Operations" data-testid="crm-create-job-title" />
          </Field>
          <Field label="Company">
            <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
              className={inputCls} placeholder="Acme Inc." data-testid="crm-create-company" />
          </Field>

          <Field label="Phone">
            <Input value={form.corporate_phone} onChange={(e) => set("corporate_phone", e.target.value)}
              className={inputCls} placeholder="+1 555 000 0000" data-testid="crm-create-phone" />
          </Field>
          <Field label="Industry">
            <Input value={form.industry} onChange={(e) => set("industry", e.target.value)}
              className={inputCls} placeholder="Healthcare" data-testid="crm-create-industry" />
          </Field>

          <Field label="Website">
            <Input value={form.website} onChange={(e) => set("website", e.target.value)}
              className={inputCls} placeholder="company.com" data-testid="crm-create-website" />
          </Field>
          <Field label="LinkedIn URL">
            <Input value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)}
              className={inputCls} placeholder="linkedin.com/in/…" data-testid="crm-create-linkedin" />
          </Field>

          <Field label="City">
            <Input value={form.city} onChange={(e) => set("city", e.target.value)}
              className={inputCls} placeholder="Denver" data-testid="crm-create-city" />
          </Field>
          <Field label="State">
            <Input value={form.state} onChange={(e) => set("state", e.target.value)}
              className={inputCls} placeholder="CO" data-testid="crm-create-state" />
          </Field>

          <Field label="Employee Count">
            <Input value={form.employee_count} onChange={(e) => set("employee_count", e.target.value)}
              type="number" min="0" className={inputCls} placeholder="50" data-testid="crm-create-employee-count" />
          </Field>
          <Field label="Estimated Seats">
            <Input value={form.estimated_seats} onChange={(e) => set("estimated_seats", e.target.value)}
              type="number" min="0" className={inputCls} placeholder="5" data-testid="crm-create-seats" />
          </Field>

          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className={inputCls} data-testid="crm-create-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                {(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {isAdmin ? (
            <Field label="Assign To">
              <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
                <SelectTrigger className={inputCls} data-testid="crm-create-assignee"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          ) : (
            <Field label="Campaign">
              <Select value={form.campaign || "none"} onValueChange={(v) => set("campaign", v === "none" ? "" : v)}>
                <SelectTrigger className={inputCls} data-testid="crm-create-campaign"><SelectValue placeholder="No campaign" /></SelectTrigger>
                <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                  <SelectItem value="none">No campaign</SelectItem>
                  {(meta?.active_campaigns || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          {isAdmin && (
            <Field label="Campaign">
              <Select value={form.campaign || "none"} onValueChange={(v) => set("campaign", v === "none" ? "" : v)}>
                <SelectTrigger className={inputCls} data-testid="crm-create-campaign"><SelectValue placeholder="No campaign" /></SelectTrigger>
                <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                  <SelectItem value="none">No campaign</SelectItem>
                  {(meta?.active_campaigns || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Next Follow-Up">
            <DatePicker value={form.next_follow_up_date} onChange={(v) => set("next_follow_up_date", v)}
              placeholder="Pick a date" testid="crm-create-follow-up" />
          </Field>

          <div className="space-y-1.5 sm:col-span-2">
            <label className="text-white/60 text-xs font-medium">Notes</label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
              className="bg-[#0A0F1E] border-white/10 text-white min-h-[80px]"
              placeholder="Context, source, or any relevant details…" data-testid="crm-create-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="text-white/60" onClick={onClose} disabled={busy} data-testid="crm-create-cancel">
            Cancel
          </Button>
          <Button className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" onClick={submit} disabled={!canSave} data-testid="crm-create-submit">
            {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Plus size={15} className="mr-1" />}
            Create Prospect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ===========================================================================
// Main component
// ===========================================================================
// ===========================================================================
// Multi-select filter (checkbox Include + Exclude toggle) with compact summary.
// Include values -> OR (backend), Exclude values -> NOT (backend). Different
// filters AND together. Provides Select All / Clear controls.
// ===========================================================================
const emptyMulti = () => ({ include: [], exclude: [] });

// Default (unfiltered) Prospect filter state. Used for initial mount, Clear
// Filters, and when leaving the Prospects list view.
const defaultFilters = () => ({
  search: "",
  assigned_to: emptyMulti(),
  campaign: emptyMulti(),
  status: emptyMulti(),
  outcome: emptyMulti(),
  follow_up: emptyMulti(),
  engagement: emptyMulti(),
  tb: emptyMulti(),
  // single-select passthrough (set only via dashboard shortcuts)
  industry: "all", opened: "all", activity_type: "all",
});

const MULTI_FILTER_FIELDS = ["assigned_to", "campaign", "status", "outcome", "follow_up", "engagement", "tb"];

const isMultiActive = (m) => ((m?.include?.length || 0) + (m?.exclude?.length || 0)) > 0;

const hasActiveFilters = (f) =>
  !!f.search ||
  f.opened !== "all" || f.industry !== "all" || f.activity_type !== "all" ||
  MULTI_FILTER_FIELDS.some((k) => isMultiActive(f[k]));

const MultiSelectFilter = ({ label, allLabel, options, value, onChange, testid, width = "w-44", counts = null }) => {
  const include = value?.include || [];
  const exclude = value?.exclude || [];
  const activeCount = include.length + exclude.length;

  const stateOf = (val) =>
    include.includes(val) ? "include" : exclude.includes(val) ? "exclude" : "none";
  const setState = (val, next) => {
    const inc = new Set(include);
    const exc = new Set(exclude);
    inc.delete(val); exc.delete(val);
    if (next === "include") inc.add(val);
    else if (next === "exclude") exc.add(val);
    onChange({ include: Array.from(inc), exclude: Array.from(exc) });
  };
  const selectAll = () => onChange({ include: options.map((o) => o.value), exclude: [] });
  const clear = () => onChange(emptyMulti());

  let summary = allLabel;
  if (activeCount > 0) {
    const parts = [];
    if (include.length) parts.push(`${include.length}`);
    if (exclude.length) parts.push(`\u2212${exclude.length}`);
    summary = `${label} (${parts.join(" \u00b7 ")})`;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`h-10 ${width} rounded-md border bg-[#0A0F1E] px-3 flex items-center justify-between gap-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6BFF]/50 ${
            activeCount ? "border-[#1E6BFF]/60 text-white" : "border-white/10 text-white/70 hover:border-white/25"
          }`}
        >
          <span className="truncate text-left">{summary}</span>
          <ChevronDown size={14} className="opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 bg-[#0E1424] border-white/10 text-white p-0" data-testid={`${testid}-panel`}>
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
          <span className="text-[11px] text-white/45 uppercase tracking-wide">{label}</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={selectAll} data-testid={`${testid}-select-all`}
              className="text-xs text-[#66B2FF] hover:text-[#66B2FF]/80">Select all</button>
            <span className="text-white/15">|</span>
            <button type="button" onClick={clear} data-testid={`${testid}-clear`}
              className="text-xs text-white/50 hover:text-white">Clear</button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {options.length === 0 ? (
            <div className="px-3 py-4 text-center text-white/35 text-sm">No options</div>
          ) : options.map((o) => {
            const st = stateOf(o.value);
            const hasCount = counts != null;
            const cnt = hasCount ? (counts[o.value] || 0) : null;
            // Zero-count options stay visible but muted; only lock interaction
            // when they are not already selected (so selections stay reversible).
            const locked = hasCount && cnt === 0 && st === "none";
            return (
              <div key={o.value}
                className={`flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 ${locked ? "opacity-45" : ""}`}
                data-testid={`${testid}-opt-${o.value}`}>
                <Checkbox
                  checked={st === "include"}
                  disabled={locked}
                  onCheckedChange={(c) => setState(o.value, c ? "include" : "none")}
                  data-testid={`${testid}-inc-${o.value}`}
                  className="border-white/25 data-[state=checked]:bg-[#2EE59D] data-[state=checked]:border-[#2EE59D] data-[state=checked]:text-[#062018]"
                />
                <span className={`flex-1 text-sm truncate ${st === "exclude" ? "text-white/40 line-through" : "text-white/85"}`}>
                  {o.label}
                </span>
                {hasCount && (
                  <span className={`text-xs tabular-nums shrink-0 ${cnt === 0 ? "text-white/30" : "text-white/45"}`}
                    data-testid={`${testid}-count-${o.value}`}>({cnt})</span>
                )}
                <button type="button"
                  disabled={locked}
                  onClick={() => setState(o.value, st === "exclude" ? "none" : "exclude")}
                  data-testid={`${testid}-exc-${o.value}`}
                  title={st === "exclude" ? "Remove exclusion" : "Exclude"}
                  aria-label={st === "exclude" ? "Remove exclusion" : "Exclude"}
                  className={`shrink-0 rounded p-1 transition-colors disabled:cursor-not-allowed ${
                    st === "exclude" ? "bg-[#FF5C7A]/20 text-[#FF5C7A]" : "text-white/30 hover:text-[#FF5C7A] hover:bg-[#FF5C7A]/10"
                  }`}>
                  <Ban size={13} />
                </button>
              </div>
            );
          })}
        </div>
        {activeCount > 0 && (
          <div className="px-3 py-2 border-t border-white/10 text-[11px] text-white/40" data-testid={`${testid}-footer`}>
            {include.length > 0 && <span className="text-[#2EE59D]">{include.length} included</span>}
            {include.length > 0 && exclude.length > 0 && <span className="mx-1">·</span>}
            {exclude.length > 0 && <span className="text-[#FF5C7A]">{exclude.length} excluded</span>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};


export const OutboundCrm = ({ token, initialView, openProspectId, openCreateNonce }) => {
  const isAdmin = hasPerm("crm.assign");
  const canManage = hasPerm("crm.manage");
  const canImport = hasPerm("crm.import");
  const canExport = hasPerm("crm.export");
  const canDelete = hasPerm("crm.delete");

  const [screen, setScreen] = useState("list"); // list | dashboard
  const [meta, setMeta] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [counts, setCounts] = useState({});

  const [view, setView] = useState(initialView || "all");
  const [filters, setFilters] = useState(defaultFilters);
  const [facets, setFacets] = useState({ campaign: null });
  const [sort, setSort] = useState({ by: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ prospects: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const [detailId, setDetailId] = useState(null);
  const [candidateWsId, setCandidateWsId] = useState(null);

  // Auto-open a prospect workspace when returning from the CRM assessment
  // (e.g. /crm?prospect=<id>), then clear the query so refresh is clean.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get("prospect");
    if (pid) {
      setDetailId(pid);
      const url = window.location.pathname;
      window.history.replaceState({}, "", url);
    }
  }, []);
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // Allow the dashboard (or other callers) to open a specific prospect or the
  // create modal without navigating away.
  useEffect(() => { if (openProspectId) setDetailId(openProspectId); }, [openProspectId]);
  useEffect(() => { if (openCreateNonce) setShowCreate(true); }, [openCreateNonce]);

  const [bulkBusy, setBulkBusy] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");

  useEffect(() => {
    crmMeta(token).then(setMeta).catch(() => {});
    crmAssignees(token).then((r) => setAssignees(r.assignees || [])).catch(() => {});
  }, [token]);

  const loadCounts = useCallback(() => {
    crmViewCounts(token).then((r) => setCounts(r.counts || {})).catch(() => {});
  }, [token]);

  // Serialize the active filter state into backend query params. Multi-select
  // fields send comma-separated include values plus a `<field>_ex` exclude list.
  const buildFilterParams = useCallback(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.opened && filters.opened !== "all") params.opened = filters.opened;
    if (filters.industry && filters.industry !== "all") params.industry = filters.industry;
    if (filters.activity_type && filters.activity_type !== "all") params.activity_type = filters.activity_type;
    ["assigned_to", "campaign", "status", "outcome", "follow_up", "engagement", "tb"].forEach((f) => {
      const inc = filters[f]?.include || [];
      const exc = filters[f]?.exclude || [];
      if (inc.length) params[f] = inc.join(",");
      if (exc.length) params[`${f}_ex`] = exc.join(",");
    });
    return params;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        view, page, page_size: 50, sort_by: sort.by, sort_dir: sort.dir,
        ...buildFilterParams(),
      };
      const res = await crmListProspects(token, params);
      setData(res);
      setSelected(new Set());
    } catch (e) {
      toast.error("Failed to load prospects");
    } finally {
      setLoading(false);
    }
  }, [token, view, page, sort, buildFilterParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts, data.total]);

  // Dynamic Campaign facet counts: reflect every active filter EXCEPT campaign.
  const loadFacets = useCallback(() => {
    crmFacets(token, "campaign", { view, ...buildFilterParams() })
      .then((r) => setFacets((prev) => ({ ...prev, campaign: r.counts || {} })))
      .catch(() => setFacets((prev) => ({ ...prev, campaign: {} })));
  }, [token, view, buildFilterParams]);

  useEffect(() => {
    if (screen === "list") loadFacets();
  }, [loadFacets, screen]);

  const refresh = () => { load(); loadCounts(); loadFacets(); };

  const changeView = (v) => { setView(v); setPage(1); };
  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const filtersActive = hasActiveFilters(filters);
  const clearFilters = () => { setFilters(defaultFilters()); setSearchDraft(""); setPage(1); };

  // Reset filters when the user leaves the Prospects list for the CRM Dashboard
  // or Deals screens. (Cross-tab navigation is reset by AdminDashboard.)
  useEffect(() => {
    if (screen !== "list") {
      setFilters(defaultFilters());
      setSearchDraft("");
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);
  const toggleSort = (field) => {
    setSort((s) => s.by === field ? { by: field, dir: s.dir === "asc" ? "desc" : "asc" } : { by: field, dir: "asc" });
  };

  const allChecked = data.prospects.length > 0 && data.prospects.every((p) => selected.has(p.id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(data.prospects.map((p) => p.id)));
  };
  const toggleOne = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const runBulk = async (body) => {
    setBulkBusy(true);
    try {
      const res = await crmBulk(token, { ...body, ids: Array.from(selected) });
      toast.success(`Updated ${res.affected} prospect(s)`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const doExport = async () => {
    try {
      const params = { view, ...buildFilterParams() };
      const res = await crmExport(token, params);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "outbound_prospects.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (!meta) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-white/50" /></div>;

  const SortHead = ({ field, children }) => (
    <TableHead className="text-white/50 cursor-pointer select-none" onClick={() => toggleSort(field)}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown size={11} className="opacity-50" /></span>
    </TableHead>
  );

  return (
    <div className="space-y-5" data-testid="outbound-crm">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Target size={20} className="text-[#66B2FF]" /> Prospects
          </h2>
          <p className="text-white/45 text-sm">Cold prospect pipeline — separate from assessment leads.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-white/10 overflow-hidden">
            <button onClick={() => setScreen("list")} className={`px-3 py-1.5 text-sm ${screen === "list" ? "bg-[#1E6BFF] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="crm-screen-list">
              <ListFilter size={14} className="inline mr-1" /> Prospects
            </button>
            <button onClick={() => setScreen("dashboard")} className={`px-3 py-1.5 text-sm ${screen === "dashboard" ? "bg-[#1E6BFF] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="crm-screen-dashboard">
              <LayoutDashboard size={14} className="inline mr-1" /> Dashboard
            </button>
            <button onClick={() => setScreen("deals")} className={`px-3 py-1.5 text-sm ${screen === "deals" ? "bg-[#1E6BFF] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="crm-screen-deals">
              <TrendingUp size={14} className="inline mr-1" /> Deals
            </button>
          </div>
          {canExport && (
            <Button variant="outline" className="border-white/15 text-white/80 h-9" onClick={doExport} data-testid="crm-export-btn">
              <Download size={15} className="mr-1" /> Export
            </Button>
          )}
          <Button variant="outline" className="border-white/15 text-white/80 h-9" onClick={refresh} disabled={loading} data-testid="crm-refresh-btn" title="Re-fetch prospects (keeps filters, search & sort)">
            <RefreshCw size={15} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {canManage && (
            <Button className="bg-[#2EE59D] hover:bg-[#2EE59D]/90 text-[#062018] font-medium h-9" onClick={() => setShowCreate(true)} data-testid="crm-new-prospect-btn">
              <Plus size={15} className="mr-1" /> New Prospect
            </Button>
          )}
          {canImport && (
            <Button className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90 h-9" onClick={() => setShowImport(true)} data-testid="crm-import-btn">
              <Upload size={15} className="mr-1" /> Import Prospects
            </Button>
          )}
        </div>
      </div>

      {screen === "dashboard" ? (
        <CrmDashboard token={token} onOpenProspect={setDetailId}
          onOpenFilter={() => {
            setScreen("list");
            setView("all");
            setFilters((f) => ({ ...f, opened: "yes", engagement: emptyMulti() }));
            setPage(1);
          }}
          onEngagedFilter={() => {
            setScreen("list");
            setView("all");
            setFilters((f) => ({ ...f, opened: "all", engagement: { include: ["engaged"], exclude: [] } }));
            setSort({ by: "engagement", dir: "desc" });
            setPage(1);
          }}
          onTbFilter={(stage) => {
            const tbVal = stage && stage !== "any" ? stage : "activity";
            setScreen("list");
            setView("all");
            setFilters((f) => ({ ...f, opened: "all", engagement: emptyMulti(), tb: { include: [tbVal], exclude: [] } }));
            setPage(1);
          }} />
      ) : screen === "deals" ? (
        <CrmDeals token={token} onOpenProspect={setDetailId} />
      ) : (
        <>
          {/* Quick view tabs */}
          <div className="flex flex-wrap gap-1.5">
            {QUICK_VIEWS.map(([key, label]) => (
              <button key={key} onClick={() => changeView(key)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${view === key ? "bg-[#1E6BFF] border-[#1E6BFF] text-white" : "border-white/10 text-white/60 hover:bg-white/5"}`}
                data-testid={`crm-view-${key}`}>
                {label}
                {counts[key] != null && <span className="ml-1.5 text-xs opacity-70">{counts[key]}</span>}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
              <Input className={inputCls + " pl-9"} placeholder="Search name, company, email, title…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setFilter("search", searchDraft); }}
                data-testid="crm-search" />
            </div>
            {isAdmin && (
              <MultiSelectFilter
                label="Assignees" allLabel="All Assignees" width="w-40"
                testid="crm-filter-assigned"
                value={filters.assigned_to}
                onChange={(v) => setFilter("assigned_to", v)}
                options={[{ value: "unassigned", label: "Unassigned" },
                          ...assignees.map((a) => ({ value: a.id, label: a.name }))]}
              />
            )}
            <MultiSelectFilter
              label="Statuses" allLabel="All Status" width="w-36"
              testid="crm-filter-status"
              value={filters.status}
              onChange={(v) => setFilter("status", v)}
              options={meta.statuses.map((s) => ({ value: s, label: s }))}
            />
            <MultiSelectFilter
              label="Outcomes" allLabel="All Outcomes" width="w-40"
              testid="crm-filter-outcome"
              value={filters.outcome}
              onChange={(v) => setFilter("outcome", v)}
              options={[{ value: "__none__", label: "No Outcome" },
                        ...meta.outcomes.map((s) => ({ value: s, label: s }))]}
            />
            {meta.campaigns.length > 0 && (
              <MultiSelectFilter
                label="Campaigns" allLabel="All Campaigns" width="w-40"
                testid="crm-filter-campaign"
                value={filters.campaign}
                onChange={(v) => setFilter("campaign", v)}
                counts={facets.campaign}
                options={[{ value: "__none__", label: "No Campaign" },
                          ...meta.campaigns.map((c) => ({ value: c, label: c }))]}
              />
            )}
            <MultiSelectFilter
              label="Follow-Up" allLabel="Any Follow-Up" width="w-40"
              testid="crm-filter-followup"
              value={filters.follow_up}
              onChange={(v) => setFilter("follow_up", v)}
              options={[{ value: "overdue", label: "Overdue" },
                        { value: "today", label: "Due Today" },
                        { value: "upcoming", label: "Upcoming" },
                        { value: "none", label: "No Follow-Up" }]}
            />
            <MultiSelectFilter
              label="Engagement" allLabel="All Engagement" width="w-40"
              testid="crm-filter-engagement"
              value={filters.engagement}
              onChange={(v) => setFilter("engagement", v)}
              options={[{ value: "opened", label: "Opened+" },
                        { value: "engaged", label: "Engaged+" },
                        { value: "highly", label: "Highly Engaged" }]}
            />
            <MultiSelectFilter
              label="TeamBuilder" allLabel="All TeamBuilder" width="w-44"
              testid="crm-filter-tb"
              value={filters.tb}
              onChange={(v) => setFilter("tb", v)}
              options={[{ value: "activity", label: "Any TB Activity" },
                        { value: "visited", label: "Visited" },
                        { value: "started", label: "Started" },
                        { value: "submitted", label: "Submitted" }]}
            />
            <Select
              value={sort.by === "engagement" ? "engagement" : "recent"}
              onValueChange={(v) => { setSort(v === "engagement" ? { by: "engagement", dir: "desc" } : { by: "created_at", dir: "desc" }); setPage(1); }}
            >
              <SelectTrigger className={inputCls + " w-40"} data-testid="crm-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                <SelectItem value="recent">Sort: Newest</SelectItem>
                <SelectItem value="engagement">Sort: Most Engaged</SelectItem>
              </SelectContent>
            </Select>
            {filtersActive && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                data-testid="crm-clear-filters"
                className="h-10 text-[#FF5C7A] hover:text-[#FF5C7A] hover:bg-[#FF5C7A]/10 px-3"
              >
                <X size={15} className="mr-1" /> Clear Filters
              </Button>
            )}
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && isAdmin && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#1E6BFF]/30 bg-[#1E6BFF]/10 px-3 py-2" data-testid="crm-bulk-bar">
              <span className="text-white/80 text-sm font-medium">{selected.size} selected</span>
              <Separator orientation="vertical" className="h-5 bg-white/15" />
              <Select onValueChange={(v) => runBulk({ action: "assign", assigned_to: v === "unassigned" ? null : v })}>
                <SelectTrigger className="bg-[#0A0F1E] border-white/10 h-8 w-40 text-white text-sm" data-testid="crm-bulk-assign"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select onValueChange={(v) => runBulk({ action: "status", status: v })}>
                <SelectTrigger className="bg-[#0A0F1E] border-white/10 h-8 w-36 text-white text-sm" data-testid="crm-bulk-status"><SelectValue placeholder="Set status…" /></SelectTrigger>
                <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                  {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select
                onValueChange={(v) => {
                  if (window.confirm(`Assign campaign "${v}" to ${selected.size} prospect(s)?`))
                    runBulk({ action: "campaign", campaign: v });
                }}>
                <SelectTrigger className="bg-[#0A0F1E] border-white/10 h-8 w-40 text-white text-sm" data-testid="crm-bulk-campaign"><SelectValue placeholder="Assign campaign…" /></SelectTrigger>
                <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                  {(meta.active_campaigns || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="border-white/15 text-white/70 h-8" disabled={bulkBusy}
                onClick={() => { if (window.confirm(`Remove campaign from ${selected.size} prospect(s)?`)) runBulk({ action: "campaign", campaign: null }); }}
                data-testid="crm-bulk-campaign-remove">
                Remove Campaign
              </Button>
              {canDelete && (
                <Button size="sm" variant="outline" className="border-[#FF5C7A]/40 text-[#FF5C7A] h-8" disabled={bulkBusy}
                  onClick={() => { if (window.confirm(`Delete ${selected.size} prospect(s)? This cannot be undone.`)) runBulk({ action: "delete" }); }}
                  data-testid="crm-bulk-delete">
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              )}
              <Button size="sm" variant="ghost" className="text-white/50 h-8" onClick={() => setSelected(new Set())}>
                <X size={14} />
              </Button>
            </div>
          )}

          {/* Table */}
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  {isAdmin && (
                    <TableHead className="w-10">
                      <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="crm-select-all" />
                    </TableHead>
                  )}
                  <SortHead field="name">Name</SortHead>
                  <SortHead field="company_name">Company</SortHead>
                  <SortHead field="city">City</SortHead>
                  <SortHead field="state">State</SortHead>
                  <TableHead className="text-white/50">Industry</TableHead>
                  {isAdmin && <TableHead className="text-white/50">Assigned</TableHead>}
                  <SortHead field="status">Status</SortHead>
                  <TableHead className="text-white/50">Outcome</TableHead>
                  <TableHead className="text-white/50">Engagement</TableHead>
                  <SortHead field="last_activity_date">Last Activity</SortHead>
                  <SortHead field="next_follow_up_date">Next Follow-Up</SortHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-12"><Loader2 className="animate-spin mx-auto text-white/40" /></TableCell></TableRow>
                ) : data.prospects.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-12 text-white/40">
                    No prospects found.{canImport ? " Import an Apollo CSV to get started." : ""}
                  </TableCell></TableRow>
                ) : data.prospects.map((p) => {
                  const overdue = p.next_follow_up_date && p.next_follow_up_date < today();
                  return (
                    <TableRow key={p.id} className="border-white/10 hover:bg-white/5 cursor-pointer" data-testid="crm-row"
                      onClick={() => setDetailId(p.id)}>
                      {isAdmin && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleOne(p.id)} data-testid={`crm-select-${p.id}`} />
                        </TableCell>
                      )}
                      <TableCell className="text-white font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {p.name}
                          {p.email_opened && (
                            <MailOpen
                              size={13}
                              className="text-[#2EE59D] shrink-0"
                              data-testid="crm-row-opened"
                              title={`Opened${p.email_open_count ? ` \u00b7 ${p.email_open_count} open${p.email_open_count === 1 ? "" : "s"}` : ""}`}
                            />
                          )}
                          {(p.tb_submitted || p.tb_started || p.tb_visited) && (() => {
                            const tb = p.tb_submitted
                              ? { Icon: ClipboardCheck, cls: "text-[#2EE59D]", label: "Submitted TeamBuilder assessment" }
                              : p.tb_started
                                ? { Icon: PenLine, cls: "text-[#A78BFA]", label: "Started TeamBuilder assessment" }
                                : { Icon: Rocket, cls: "text-[#66B2FF]", label: `Visited TeamBuilder${p.tb_visit_count ? ` \u00b7 ${p.tb_visit_count} visit${p.tb_visit_count === 1 ? "" : "s"}` : ""}` };
                            const TbIcon = tb.Icon;
                            return (
                              <TbIcon size={13} className={`${tb.cls} shrink-0`} data-testid="crm-row-tb" title={tb.label} />
                            );
                          })()}
                        </span>
                      </TableCell>
                      <TableCell className="text-white/70">{p.company_name || "—"}</TableCell>
                      <TableCell className="text-white/60">{p.city || "—"}</TableCell>
                      <TableCell className="text-white/60">{p.state || "—"}</TableCell>
                      <TableCell className="text-white/60">{p.industry || "—"}</TableCell>
                      {isAdmin && <TableCell className="text-white/60">{p.assigned_to_name || <span className="text-white/30">Unassigned</span>}</TableCell>}
                      <TableCell><Badge className={`border ${statusColor(p.status)}`}>{p.status}</Badge></TableCell>
                      <TableCell className="text-white/60 text-sm">{p.outcome || "—"}</TableCell>
                      <TableCell data-testid="crm-row-engagement">
                        {(() => {
                          const lvl = engagementLevel(p);
                          if (lvl === "None") return <span className="text-white/25 text-sm">—</span>;
                          const meta = ENGAGEMENT_META[lvl] || ENGAGEMENT_META.None;
                          return (
                            <div className="flex flex-col gap-0.5">
                              <Badge className={`border w-fit ${meta.cls}`}>{lvl}</Badge>
                              <span className="text-white/35 text-[11px]">
                                {(p.email_open_count || 0)} open{(p.email_open_count === 1) ? "" : "s"}
                                {p.email_last_opened_at ? ` \u00b7 ${fmtDateTime(p.email_last_opened_at)}` : ""}
                              </span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-white/50 text-sm">{fmtDate(p.last_activity_date)}</TableCell>
                      <TableCell className={`text-sm ${overdue ? "text-[#FF5C7A]" : "text-white/50"}`}>{fmtDate(p.next_follow_up_date)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-white/50">
            <span data-testid="crm-total">{data.total} prospect{data.total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-white/15 text-white/70 h-8" disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="crm-prev">
                <ChevronLeft size={15} />
              </Button>
              <span>Page {data.page} of {data.pages || 1}</span>
              <Button size="sm" variant="outline" className="border-white/15 text-white/70 h-8" disabled={page >= (data.pages || 1)}
                onClick={() => setPage((p) => p + 1)} data-testid="crm-next">
                <ChevronRight size={15} />
              </Button>
            </div>
          </div>
        </>
      )}

      {detailId && (
        <ProspectDetail token={token} id={detailId} meta={meta} assignees={assignees} isAdmin={isAdmin}
          onClose={() => setDetailId(null)} onChanged={refresh} onOpenCandidate={setCandidateWsId} />
      )}
      {candidateWsId && (
        <CandidateWorkspace token={token} id={candidateWsId}
          onClose={() => setCandidateWsId(null)} />
      )}
      {showCreate && (
        <CreateProspectModal token={token} meta={meta} assignees={assignees} isAdmin={isAdmin}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { refresh(); if (id) setDetailId(id); }} />
      )}
      {showImport && (
        <ImportWizard token={token} meta={meta} assignees={assignees}
          onClose={() => { setShowImport(false); refresh(); }} onDone={refresh} />
      )}
    </div>
  );
};
